import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { createPortal } from 'react-dom';
import {
  ExclamationTriangleIcon,
  ClockIcon,
  CheckCircleIcon,
  ChevronUpDownIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  ArrowTopRightOnSquareIcon,
  UserIcon,
  XMarkIcon,
  MagnifyingGlassIcon,
  Cog6ToothIcon,
  MapPinIcon,
  HandRaisedIcon,
} from '@heroicons/react/24/outline';
import { LinkIcon } from '@heroicons/react/24/solid';
import { Users } from 'lucide-react';
import { useMsal } from '@azure/msal-react';
import { useDeadlineCheck } from '../../hooks/useDeadlineCheck';
import ReasonNoteModal from '../../components/ReasonNoteModal';
import dispatchRemovalService from '../../services/dispatchRemoval.service';
import MultiSelect, { MultiSelectOption } from '../../components/MultiSelect';
import { OrderDispatcherFilter, DriverDispatcherFilter } from '../../components/filters';
import GridLabel from '../../components/GridLabel';
import FilterBar from '../../components/FilterBar';
import { useGlobalFilters } from '../../contexts/GlobalFiltersContext';
import Toggle from '../../components/Toggle';
import { useDisplayPreferences } from '../../contexts/DisplayPreferencesContext';
import { useNotificationPreferences } from '../../contexts/NotificationPreferencesContext';
import { usePermissions } from '../../contexts/PermissionContext';
import { PermissionAwareInput, PermissionAwareButton, PermissionAwareSelect } from '../../contexts/PermissionAccessContext';
import PermissionsContainer from '../../components/PermissionsContainer';
import { useToast } from '../../contexts/ToastContext';
import { useRealtimeCollaboration } from '../../hooks/useRealtimeCollaboration';
import { useDispatchFooter } from '../../contexts/DispatchFooterContext';
import { useWorkingStatusPreference } from '../../contexts/WorkingStatusPreferenceContext';
import { Tooltip } from '../../components/ui/Tooltip';
import ProDetailSlideOver from '../../components/pro/ProDetailSlideOver';
import ordersService from '../../services/orders.service';
import api from '../../services/api';
import userSettingsService from '../../services/userSettings.service';
import { GoogleMap, useJsApiLoader } from '@react-google-maps/api';
import { getGoogleMapsLoaderOptions } from '../../config/googleMaps';
import styles from './DriverStatusMonitor.module.css';
import { useVisibilityAwareInterval } from '../../hooks/useVisibilityAwareInterval';
import { toLocalDateKey } from '../../utils/dateUtils';
import { proDetailPath } from '../../utils/proSearch';

// Extracted reusable components
import {
  RiskSection,
  RemoveConfirmModal,
  SettingsDrawer,
  formatMinutes,
  driverMatchesTruckTypes,
  driverMatchesSizes,
  driverMatchesChassisTypes,
  driverMatchesDutyStatuses,
  NO_HOS_DUTY_STATUS,
  computeDayStartStats,
  nextDeliveryAppt,
  compareByDispatcher,
  compareByStartTime,
  clearRequestingUnit,
  toastIdsForUnit,
} from '../../components/driver-status-monitor';
import type {
  DispatchRequest,
  DisplaySettings,
  MoveRisk,
  DriverWithMoves,
  DriverRiskData,
  MoveGroup,
  DragState,
  GhostSuggestion,
} from '../../components/driver-status-monitor';
import { GhostAssignModal } from '../../components/GhostAssignModal';
import { DEFAULT_DISPLAY_SETTINGS } from '../../components/driver-status-monitor';
import { DriverCommentModal } from '../../components/driver-status-monitor/DriverCommentModal';
import { useDriverStatusLegend } from '../../components/driver-status-monitor/useDriverStatusLegend';

const REFRESH_INTERVAL = 30000;
const DISPLAY_SETTINGS_KEY = 'driver_status_monitor_v2_display_settings';

// ============================================================================
// Google Maps Components (page-specific, kept inline)
// ============================================================================

// Create truck icon element for map markers (using same SVG as LiveMap)
function createTruckMarkerElement(): HTMLElement {
  const size = 60;
  const color = '#3B82F6';
  const strokeColor = '#000000';

  const container = document.createElement('div');
  container.style.width = `${size}px`;
  container.style.height = `${size}px`;
  container.style.transform = 'translate(-50%, -50%)';
  container.style.position = 'absolute';
  container.style.cursor = 'pointer';

  container.innerHTML = `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 62 18" width="${size}" height="${size}">
      <g transform="translate(31, 9) rotate(0) translate(-31, -9)">
        <g transform="translate(-1.4, -22.39)">
          <path d="M43.03,38.59L44.6,34.39L44.6,24.99C44.6,24.66 44.33,24.39 44,24.39L43.6,24.39C43.6,24.39 1.41,24.005 1.41,24.335L1.4,34.99C1.4,35.32 1.67,35.59 2,35.59L4.82,35.59C4.56,35.99 4.4,36.47 4.4,36.99C4.4,38.42 5.57,39.59 7,39.59C7.81,39.59 8.52,39.21 9,38.63C9.48,39.21 10.19,39.59 11,39.59C11.81,39.59 12.52,39.21 13,38.63C13.48,39.21 14.19,39.59 15,39.59C16.43,39.59 17.6,38.42 17.6,36.99C17.6,36.47 17.44,35.99 17.18,35.59L19.4,35.59L19.4,36.99C19.4,37.32 19.67,37.59 20,37.59L31,37.59C31.33,37.59 31.6,37.32 31.6,36.99L31.6,35.59L34.82,35.59C34.56,35.99 34.4,36.47 34.4,36.99C34.4,38.42 35.57,39.59 37,39.59C37.81,39.59 38.52,39.21 39,38.63C39.48,39.21 40.19,39.59 41,39.59C41.81,39.59 42.56,39.19 43.03,38.59ZM7,38.4C6.23,38.4 5.6,37.77 5.6,37C5.6,36.23 6.23,35.6 7,35.6C7.77,35.6 8.4,36.23 8.4,37C8.4,37.77 7.77,38.4 7,38.4ZM11,38.4C10.23,38.4 9.6,37.77 9.6,37C9.6,36.23 10.23,35.6 11,35.6C11.77,35.6 12.4,36.23 12.4,37C12.4,37.77 11.77,38.4 11,38.4ZM16.4,37C16.4,37.77 15.77,38.4 15,38.4C14.23,38.4 13.6,37.77 13.6,37C13.6,36.23 14.23,35.6 15,35.6C15.77,35.6 16.4,36.23 16.4,37ZM30.4,36.4L20.6,36.4L20.6,35.6L30.4,35.6L30.4,36.4ZM37,38.4C36.23,38.4 35.6,37.77 35.6,37C35.6,36.23 36.23,35.6 37,35.6C37.77,35.6 38.4,36.23 38.4,37C38.4,37.77 37.77,38.4 37,38.4ZM41,38.4C40.23,38.4 39.6,37.77 39.6,37C39.6,36.23 40.23,35.6 41,35.6C41.77,35.6 42.4,36.23 42.4,37C42.4,37.77 41.77,38.4 41,38.4Z"
            fill="${color}"
            stroke="${strokeColor}"
            stroke-width="0.4"/>
          <g transform="translate(0.033063, 0.0104117)">
            <path d="M62.12,32.41L57.48,31.48C57.45,31.39 57.42,31.29 57.39,31.2C57.34,31.05 57.27,30.91 57.21,30.77C57.21,30.75 57.2,30.73 57.19,30.72C56.82,29.88 56.29,29.09 55.6,28.43C54.62,27.5 53.41,26.86 52.08,26.6L46.12,25.41C45.94,25.38 45.76,25.42 45.62,25.53C45.48,25.64 45.4,25.81 45.4,25.99L45.4,34.39L44.6,34.39C44.6,34.06 34.508,34.268 34.508,34.268C34.248,34.668 34.4,36.47 34.4,36.99C34.4,38.42 35.57,39.59 37,39.59C37.81,39.59 38.52,39.21 39,38.63C39.48,39.21 40.19,39.59 41,39.59C41.81,39.59 42.56,39.19 43.03,38.59L55.98,38.59C56.47,39.21 57.19,39.59 58,39.59C58.81,39.59 59.53,39.21 60.02,38.59L62,38.59C62.33,38.59 62.6,38.32 62.6,37.99L62.6,32.99C62.6,32.7 62.4,32.46 62.12,32.4L62.12,32.41ZM54.759,29.641C54.795,29.701 55.271,30.101 55.271,30.101L55.551,30.485L55.848,31.013C55.848,31.013 56.091,31.592 56.093,31.595L51.561,31.519L51.507,29.086C51.507,29.086 52.623,29.018 53.165,29.111C53.445,29.158 54.236,29.33 54.236,29.33L54.759,29.641ZM37,38.4C36.23,38.4 35.6,37.77 35.6,37C35.6,36.23 36.23,35.6 37,35.6C37.77,35.6 38.4,36.23 38.4,37C38.4,37.77 37.77,38.4 37,38.4ZM41,38.4C40.23,38.4 39.6,37.77 39.6,37C39.6,36.23 40.23,35.6 41,35.6C41.77,35.6 42.4,36.23 42.4,37C42.4,37.77 41.77,38.4 41,38.4ZM56.79,37.7C56.67,37.49 56.6,37.25 56.6,37C56.6,36.23 57.23,35.6 58,35.6C58.77,35.6 59.4,36.23 59.4,37C59.4,37.24 59.33,37.49 59.21,37.7C58.7,38.58 57.3,38.58 56.79,37.7Z"
              fill="${color}"
              stroke="${strokeColor}"
              stroke-width="0.4"/>
          </g>
        </g>
      </g>
    </svg>
  `;

  return container;
}

// Current Location Mini Map Component
function CurrentLocationMiniMap({
  driverLat,
  driverLng,
  originLat,
  originLng,
  destLat,
  destLng,
}: {
  driverLat: number;
  driverLng: number;
  originLat?: number | null;
  originLng?: number | null;
  destLat?: number | null;
  destLng?: number | null;
}) {
  const mapRef = useRef<google.maps.Map | null>(null);
  const truckMarkerRef = useRef<google.maps.marker.AdvancedMarkerElement | null>(null);
  const originMarkerRef = useRef<google.maps.marker.AdvancedMarkerElement | null>(null);
  const destMarkerRef = useRef<google.maps.marker.AdvancedMarkerElement | null>(null);
  const directionsRendererRef = useRef<google.maps.DirectionsRenderer | null>(null);

  const center = useMemo(() => ({ lat: driverLat, lng: driverLng }), [driverLat, driverLng]);

  const onMapLoad = useCallback((map: google.maps.Map) => {
    mapRef.current = map;

    const truckElement = createTruckMarkerElement();
    truckMarkerRef.current = new google.maps.marker.AdvancedMarkerElement({
      position: { lat: driverLat, lng: driverLng },
      map,
      content: truckElement,
      title: 'Current Location',
      zIndex: 100,
    });

    if (originLat && originLng && destLat && destLng) {
      if (window.google?.maps?.marker?.PinElement) {
        const originPin = new google.maps.marker.PinElement({
          background: 'rgb(var(--c-emerald-500))',
          borderColor: 'rgb(var(--c-white))',
          glyphColor: '#ffffff',
          scale: 0.8,
        });
        originMarkerRef.current = new google.maps.marker.AdvancedMarkerElement({
          position: { lat: originLat, lng: originLng },
          map,
          content: originPin.element,
          title: 'Origin',
          zIndex: 50,
        });

        const destPin = new google.maps.marker.PinElement({
          background: 'rgb(var(--c-red-500))',
          borderColor: 'rgb(var(--c-white))',
          glyphColor: '#ffffff',
          scale: 0.8,
        });
        destMarkerRef.current = new google.maps.marker.AdvancedMarkerElement({
          position: { lat: destLat, lng: destLng },
          map,
          content: destPin.element,
          title: 'Destination',
          zIndex: 50,
        });
      }

      const directionsService = new google.maps.DirectionsService();
      directionsService.route(
        {
          origin: { lat: originLat, lng: originLng },
          destination: { lat: destLat, lng: destLng },
          travelMode: google.maps.TravelMode.DRIVING,
        },
        (result, status) => {
          if (status === google.maps.DirectionsStatus.OK && result) {
            directionsRendererRef.current = new google.maps.DirectionsRenderer({
              map,
              suppressMarkers: true,
              polylineOptions: {
                strokeColor: '#3B82F6',
                strokeWeight: 4,
                strokeOpacity: 0.7,
              },
            });
            directionsRendererRef.current.setDirections(result);

            if (result.routes[0]?.bounds) {
              const bounds = result.routes[0].bounds;
              bounds.extend({ lat: driverLat, lng: driverLng });
              map.fitBounds(bounds, { top: 30, right: 30, bottom: 30, left: 30 });
            }
          }
        }
      );
    }
  }, [driverLat, driverLng, originLat, originLng, destLat, destLng]);

  useEffect(() => {
    return () => {
      if (truckMarkerRef.current) truckMarkerRef.current.map = null;
      if (originMarkerRef.current) originMarkerRef.current.map = null;
      if (destMarkerRef.current) destMarkerRef.current.map = null;
      if (directionsRendererRef.current) directionsRendererRef.current.setMap(null);
    };
  }, []);

  return (
    <GoogleMap
      mapContainerStyle={{ width: '50vw', height: '50vh' }}
      center={center}
      zoom={10}
      onLoad={onMapLoad}
      options={{
        mapId: '3cf64d0fcebdaf11f3625d6e',
        disableDefaultUI: true,
        zoomControl: true,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        clickableIcons: false,
      }}
    />
  );
}

// Deadhead Map Component with Directions
function DeadheadMap({
  fromLat,
  fromLng,
  toLat,
  toLng,
}: {
  fromLat: number;
  fromLng: number;
  toLat: number;
  toLng: number;
}) {
  const mapRef = useRef<google.maps.Map | null>(null);
  const directionsRendererRef = useRef<google.maps.DirectionsRenderer | null>(null);
  const originMarkerRef = useRef<google.maps.marker.AdvancedMarkerElement | null>(null);
  const destMarkerRef = useRef<google.maps.marker.AdvancedMarkerElement | null>(null);

  const center = useMemo(() => ({
    lat: (fromLat + toLat) / 2,
    lng: (fromLng + toLng) / 2,
  }), [fromLat, fromLng, toLat, toLng]);

  const onMapLoad = useCallback((map: google.maps.Map) => {
    mapRef.current = map;

    if (window.google?.maps?.marker?.PinElement) {
      const originPin = new google.maps.marker.PinElement({
        background: 'rgb(var(--c-red-500))',
        borderColor: 'rgb(var(--c-white))',
        glyphColor: '#ffffff',
      });
      originMarkerRef.current = new google.maps.marker.AdvancedMarkerElement({
        position: { lat: fromLat, lng: fromLng },
        map,
        content: originPin.element,
        title: 'From',
      });

      const destPin = new google.maps.marker.PinElement({
        background: 'rgb(var(--c-emerald-500))',
        borderColor: 'rgb(var(--c-white))',
        glyphColor: '#ffffff',
      });
      destMarkerRef.current = new google.maps.marker.AdvancedMarkerElement({
        position: { lat: toLat, lng: toLng },
        map,
        content: destPin.element,
        title: 'To',
      });
    }

    const directionsService = new google.maps.DirectionsService();
    directionsService.route(
      {
        origin: { lat: fromLat, lng: fromLng },
        destination: { lat: toLat, lng: toLng },
        travelMode: google.maps.TravelMode.DRIVING,
      },
      (result, status) => {
        if (status === google.maps.DirectionsStatus.OK && result) {
          directionsRendererRef.current = new google.maps.DirectionsRenderer({
            map,
            suppressMarkers: true,
            polylineOptions: {
              strokeColor: '#6366F1',
              strokeWeight: 4,
              strokeOpacity: 0.8,
            },
          });
          directionsRendererRef.current.setDirections(result);

          if (result.routes[0]?.bounds) {
            map.fitBounds(result.routes[0].bounds, { top: 40, right: 40, bottom: 40, left: 40 });
          }
        }
      }
    );
  }, [fromLat, fromLng, toLat, toLng]);

  useEffect(() => {
    return () => {
      if (directionsRendererRef.current) directionsRendererRef.current.setMap(null);
      if (originMarkerRef.current) originMarkerRef.current.map = null;
      if (destMarkerRef.current) destMarkerRef.current.map = null;
    };
  }, []);

  return (
    <GoogleMap
      mapContainerStyle={{ width: '100%', height: '100%' }}
      center={center}
      zoom={8}
      onLoad={onMapLoad}
      options={{
        mapId: '3cf64d0fcebdaf11f3625d6e',
        disableDefaultUI: false,
        zoomControl: true,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        clickableIcons: false,
      }}
    />
  );
}

// Available Moves Map Component
function AvailableMovesMap({
  fromLat,
  fromLng,
  toLat,
  toLng,
  availablePros,
  onBoundsChanged,
  onProClick,
  selectedPros,
}: {
  fromLat: number;
  fromLng: number;
  toLat: number;
  toLng: number;
  availablePros: any[];
  onBoundsChanged: (bounds: google.maps.LatLngBounds) => void;
  onProClick: (proNumbers: number[]) => void;
  selectedPros: number[];
}) {
  const mapRef = useRef<google.maps.Map | null>(null);
  const directionsRendererRef = useRef<google.maps.DirectionsRenderer | null>(null);
  const originMarkerRef = useRef<google.maps.marker.AdvancedMarkerElement | null>(null);
  const destMarkerRef = useRef<google.maps.marker.AdvancedMarkerElement | null>(null);
  const proMarkersRef = useRef<google.maps.marker.AdvancedMarkerElement[]>([]);
  const idleListenerRef = useRef<google.maps.MapsEventListener | null>(null);

  const center = useMemo(() => ({
    lat: (fromLat + toLat) / 2,
    lng: (fromLng + toLng) / 2,
  }), [fromLat, fromLng, toLat, toLng]);

  const parseCoordinates = useCallback((coords: string | null): { lat: number; lng: number } | null => {
    if (!coords) return null;
    const firstCoordPair = coords.split(/[\r\n;]+/)[0].trim();
    const parts = firstCoordPair.split(',').map(s => parseFloat(s.trim()));
    if (parts.length === 2 && !isNaN(parts[0]) && !isNaN(parts[1])) {
      if (Math.abs(parts[0]) > 90 && Math.abs(parts[1]) < 90) {
        return { lat: parts[1], lng: parts[0] };
      }
      return { lat: parts[0], lng: parts[1] };
    }
    return null;
  }, []);

  const createContainerIcon = useCallback((proNumber: string | number, isSelected: boolean = false) => {
    const container = document.createElement('div');
    container.style.cssText = `
      background: ${isSelected ? '#F59E0B' : '#F3E8FF'};
      border: 2px solid ${isSelected ? '#D97706' : '#A855F7'};
      border-radius: 4px;
      padding: 4px 8px;
      font-size: 12px;
      font-weight: bold;
      color: ${isSelected ? 'white' : '#7E22CE'};
      white-space: nowrap;
      box-shadow: 0 2px 4px rgba(0,0,0,0.2);
      cursor: pointer;
      transform: scale(${isSelected ? '1.1' : '1'});
    `;
    container.textContent = String(proNumber);
    return container;
  }, []);

  const createClusterIcon = useCallback((count: number, isSelected: boolean = false) => {
    const container = document.createElement('div');
    container.style.cssText = `
      background: ${isSelected ? '#F59E0B' : '#F3E8FF'};
      border: 3px solid ${isSelected ? '#D97706' : '#A855F7'};
      border-radius: 50%;
      width: 40px;
      height: 40px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 14px;
      font-weight: bold;
      color: ${isSelected ? 'white' : '#7E22CE'};
      box-shadow: 0 2px 6px rgba(0,0,0,0.3);
      cursor: pointer;
      transform: scale(${isSelected ? '1.1' : '1'});
    `;
    container.textContent = String(count);
    return container;
  }, []);

  const clusterPros = useCallback((pros: any[]) => {
    const clusters: { [key: string]: any[] } = {};
    const CLUSTER_DISTANCE = 0.001;

    pros.forEach(pro => {
      const coords = parseCoordinates(pro.originCoordinates);
      if (!coords) return;

      const clusterKey = `${Math.round(coords.lat / CLUSTER_DISTANCE)}_${Math.round(coords.lng / CLUSTER_DISTANCE)}`;
      if (!clusters[clusterKey]) {
        clusters[clusterKey] = [];
      }
      clusters[clusterKey].push({ ...pro, coords });
    });

    return Object.values(clusters);
  }, [parseCoordinates]);

  useEffect(() => {
    if (!mapRef.current) return;

    proMarkersRef.current.forEach(marker => {
      marker.map = null;
    });
    proMarkersRef.current = [];

    if (availablePros.length === 0) return;

    const clusters = clusterPros(availablePros);

    const newMarkers = clusters.map(cluster => {
      if (cluster.length === 0) return null;

      const centerCoords = cluster[0].coords;
      if (!centerCoords) return null;

      let iconElement: HTMLElement;
      let marker: google.maps.marker.AdvancedMarkerElement;

      const clusterProNumbers = cluster.map((p: any) => p.pronumber);
      const isSelected = clusterProNumbers.some((pn: number) => selectedPros.includes(pn));

      if (cluster.length === 1) {
        const pro = cluster[0];
        iconElement = createContainerIcon(pro.pronumber, isSelected);
        marker = new google.maps.marker.AdvancedMarkerElement({
          position: centerCoords,
          map: mapRef.current,
          content: iconElement,
          title: `PRO: ${pro.pronumber} - ${pro.originName}`,
          zIndex: isSelected ? 1500 : 1000,
        });
      } else {
        iconElement = createClusterIcon(cluster.length, isSelected);
        marker = new google.maps.marker.AdvancedMarkerElement({
          position: centerCoords,
          map: mapRef.current,
          content: iconElement,
          title: `${cluster.length} Pros at this location`,
          zIndex: isSelected ? 1500 : 1000,
        });
      }

      marker.addListener('click', () => {
        onProClick(clusterProNumbers);
      });

      return marker;
    }).filter(m => m !== null) as google.maps.marker.AdvancedMarkerElement[];

    proMarkersRef.current = newMarkers;
  }, [availablePros, clusterPros, createContainerIcon, createClusterIcon, onProClick, selectedPros]);

  const onMapLoad = useCallback((map: google.maps.Map) => {
    mapRef.current = map;

    const isSinglePoint = fromLat === toLat && fromLng === toLng;

    if (window.google?.maps?.marker?.PinElement) {
      const originPin = new google.maps.marker.PinElement({
        background: 'rgb(var(--c-red-500))',
        borderColor: 'rgb(var(--c-white))',
        glyphColor: '#ffffff',
      });
      originMarkerRef.current = new google.maps.marker.AdvancedMarkerElement({
        position: { lat: fromLat, lng: fromLng },
        map,
        content: originPin.element,
        title: isSinglePoint ? 'Location' : 'Driver Location',
        zIndex: 2000,
      });

      if (!isSinglePoint) {
        const destPin = new google.maps.marker.PinElement({
          background: 'rgb(var(--c-emerald-500))',
          borderColor: 'rgb(var(--c-white))',
          glyphColor: '#ffffff',
        });
        destMarkerRef.current = new google.maps.marker.AdvancedMarkerElement({
          position: { lat: toLat, lng: toLng },
          map,
          content: destPin.element,
          title: 'Destination',
          zIndex: 2000,
        });
      }
    }

    if (!isSinglePoint) {
      const directionsService = new google.maps.DirectionsService();
      directionsService.route(
        {
          origin: { lat: fromLat, lng: fromLng },
          destination: { lat: toLat, lng: toLng },
          travelMode: google.maps.TravelMode.DRIVING,
        },
        (result, status) => {
          if (status === google.maps.DirectionsStatus.OK && result) {
            directionsRendererRef.current = new google.maps.DirectionsRenderer({
              map,
              suppressMarkers: true,
              polylineOptions: {
                strokeColor: '#6366F1',
                strokeWeight: 4,
                strokeOpacity: 0.8,
              },
            });
            directionsRendererRef.current.setDirections(result);

            if (result.routes[0]?.bounds) {
              map.fitBounds(result.routes[0].bounds, { top: 40, right: 40, bottom: 40, left: 40 });
            }
          }
        }
      );
    } else {
      map.setCenter({ lat: fromLat, lng: fromLng });
      map.setZoom(10);
    }

    idleListenerRef.current = map.addListener('idle', () => {
      const bounds = map.getBounds();
      if (bounds) {
        onBoundsChanged(bounds);
      }
    });
  }, [fromLat, fromLng, toLat, toLng, onBoundsChanged]);

  useEffect(() => {
    return () => {
      if (directionsRendererRef.current) directionsRendererRef.current.setMap(null);
      if (originMarkerRef.current) originMarkerRef.current.map = null;
      if (destMarkerRef.current) destMarkerRef.current.map = null;
      proMarkersRef.current.forEach(marker => { marker.map = null; });
      proMarkersRef.current = [];
      if (idleListenerRef.current) google.maps.event.removeListener(idleListenerRef.current);
    };
  }, []);

  return (
    <GoogleMap
      mapContainerStyle={{ width: '100%', height: '100%' }}
      center={center}
      zoom={8}
      onLoad={onMapLoad}
      options={{
        mapId: '3cf64d0fcebdaf11f3625d6e',
        disableDefaultUI: false,
        zoomControl: true,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        clickableIcons: false,
      }}
    />
  );
}

// ============================================================================
// Main Component
// ============================================================================

function DriverStatusMonitor() {
  useDriverStatusLegend();
  const { showGridHighlight, showGridLabels, showGridBorders } = useDisplayPreferences();
  const { permissions } = usePermissions();
  const { showDriverRequestToast, showOnlyMyDrivers } = useNotificationPreferences();
  const { showToast, dismissToast } = useToast();
  const [searchParams] = useSearchParams();
  const { accounts } = useMsal();
  const currentUser = accounts[0];

  // Real-time collaboration
  const { activeUsers, socket } = useRealtimeCollaboration({
    pageId: 'driver-status-monitor',
    userName: currentUser?.name || currentUser?.username || 'Anonymous',
    userId: currentUser?.localAccountId || currentUser?.homeAccountId,
    userEmail: currentUser?.username,
  });

  const { setDispatchButton } = useDispatchFooter();

  // "Send to gwiMove" toggle for this page. Initial value comes from the user's
  // defaultSendToGwiMove preference; the footer toggle lets them override it for
  // the moves they are working now without changing the global setting. Applies
  // to every driver assignment made here -- available-move assign, quick
  // reassign, and ghost-card assign alike. Same pattern as the Dispatch Planner.
  const { defaultSendToGwiMove } = useWorkingStatusPreference();
  const [sendToGwiMove, setSendToGwiMove] = useState(defaultSendToGwiMove);
  // Resync if the user changes the global setting in the settings panel. This is
  // same-tab only -- the preference context writes localStorage but has no storage-
  // event listener, so a change made in another tab does not reach this one.
  useEffect(() => { setSendToGwiMove(defaultSendToGwiMove); }, [defaultSendToGwiMove]);

  // Data state
  const [data, setData] = useState<DriverRiskData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Dispatch request tracking
  const [dispatchRequestUnitnumbers, setDispatchRequestUnitnumbers] = useState<Set<string>>(new Set());
  const [requestingDrivers, setRequestingDrivers] = useState<DriverWithMoves[]>([]);
  const [dispatchRequestCreatedAt, setDispatchRequestCreatedAt] = useState<Map<string, string>>(new Map());
  const dispatchRequestsRef = useRef<DispatchRequest[]>([]);
  const shownToastRecnosRef = useRef<Set<number>>(new Set());
  // recno -> live toast id, so acknowledging a request can close the persistent
  // "requested more work" toast it just made obsolete.
  const requestToastIdsRef = useRef<Map<number, number>>(new Map());

  // Loading timer state
  const [loadStartTime, setLoadStartTime] = useState<number | null>(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  // Global filters
  const {
    selectedRegions,
    setSelectedRegions,
    selectedOrderDispatchers,
    setSelectedOrderDispatchers,
    selectedDriverDispatchers,
    setSelectedDriverDispatchers,
    isLoading: globalFiltersLoading,
  } = useGlobalFilters();

  // Date filter (defaults to today)
  const [selectedDate, setSelectedDate] = useState(() => {
    const now = new Date();
    return now.toLocaleDateString('en-CA'); // YYYY-MM-DD format
  });
  const dateRange = useMemo(() => {
    return { start_date: selectedDate, end_date: selectedDate };
  }, [selectedDate]);

  // Local filters
  const [regionOptions, setRegionOptions] = useState<MultiSelectOption[]>([]);
  const [selectedTruckTypes, setSelectedTruckTypes] = useState<string[]>([]);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [selectedDutyStatuses, setSelectedDutyStatuses] = useState<string[]>([]);
  const [selectedSizes, setSelectedSizes] = useState<string[]>([]);
  const [selectedChassisTypes, setSelectedChassisTypes] = useState<string[]>([]);
  const [showOnlyRequests, setShowOnlyRequests] = useState(false);
  const localFiltersLoaded = useRef(false);

  // Load local filters from user settings
  useEffect(() => {
    if (localFiltersLoaded.current) return;
    const loadLocalFilters = async () => {
      try {
        const setting = await userSettingsService.getUserSetting('driver_status_monitor_v2_filters');
        if (setting?.setting_value) {
          const filters = setting.setting_value;
          if (Array.isArray(filters.selectedTruckTypes)) {
            setSelectedTruckTypes(filters.selectedTruckTypes.filter((type: string) => type === 'LOCAL' || type === 'OTR'));
          }
          if (filters.selectedStatuses) setSelectedStatuses(filters.selectedStatuses);
          if (filters.selectedDutyStatuses) setSelectedDutyStatuses(filters.selectedDutyStatuses);
          if (filters.selectedSizes) setSelectedSizes(filters.selectedSizes);
          if (filters.selectedChassisTypes) setSelectedChassisTypes(filters.selectedChassisTypes);
        }
      } catch (error) {
        // Silently fail
      }
      localFiltersLoaded.current = true;
    };
    loadLocalFilters();
  }, []);

  // Auto-save local filters
  const saveLocalFiltersTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  useEffect(() => {
    if (!localFiltersLoaded.current) return;

    if (saveLocalFiltersTimeoutRef.current) clearTimeout(saveLocalFiltersTimeoutRef.current);
    saveLocalFiltersTimeoutRef.current = setTimeout(async () => {
      try {
        await userSettingsService.saveUserSetting('driver_status_monitor_v2_filters', {
          selectedTruckTypes,
          selectedStatuses,
          selectedDutyStatuses,
          selectedSizes,
          selectedChassisTypes,
        });
      } catch (error) {
        console.error('Failed to save local filters:', error);
      }
    }, 500);

    return () => {
      if (saveLocalFiltersTimeoutRef.current) clearTimeout(saveLocalFiltersTimeoutRef.current);
    };
  }, [selectedTruckTypes, selectedStatuses, selectedDutyStatuses, selectedSizes, selectedChassisTypes]);

  // Static options for driver status filter
  const driverStatusOptions: MultiSelectOption[] = [
    { value: 'not_started', label: 'Not Started' },
    { value: 'between_moves', label: 'Between Moves' },
    { value: 'in_progress', label: 'In Progress' },
  ];

  // Static options for the Samsara HOS duty status filter. Values are
  // DutyStatusKey buckets (see normalizeDutyStatus) plus a No HOS option for
  // drivers with no HOS record at all.
  const dutyStatusOptions: MultiSelectOption[] = [
    { value: 'driving', label: 'Driving' },
    { value: 'on_duty', label: 'On Duty' },
    { value: 'yard_move', label: 'Yard Move' },
    { value: 'off_duty', label: 'Off Duty' },
    { value: 'sleeper', label: 'Sleeper' },
    { value: 'personal', label: 'Personal' },
    { value: NO_HOS_DUTY_STATUS, label: 'No HOS Data' },
  ];

  // Static options for container size filter (values match orderscurrent.size)
  const sizeOptions: MultiSelectOption[] = [
    { value: '20', label: "20'" },
    { value: '40', label: "40'" },
    { value: '45', label: "45'" },
    { value: '48', label: "48'" },
    { value: '53', label: "53'" },
  ];

  // Static options for chassis type filter (values match orderscurrent.chassistype)
  const chassisOptions: MultiSelectOption[] = [
    { value: 'STANDARD', label: 'Standard' },
    { value: 'ULTRA LIGHT', label: 'Ultra Light' },
    { value: 'TRIAXLE', label: 'Triaxle' },
    { value: '93K TRIAXLE', label: '93K Triaxle' },
    { value: 'SUPER LIGHT', label: 'Super Light' },
  ];

  // Sort options
  type DriverSortOption = 'unit_number' | 'start_time' | 'hos_remaining' | 'completed_count' | 'remaining_count' | 'status' | 'delivery_appt' | 'dispatcher';
  const sortOptions: { value: DriverSortOption; label: string }[] = [
    { value: 'unit_number', label: 'Unit Number' },
    { value: 'start_time', label: 'Start Time' },
    { value: 'hos_remaining', label: 'HOS Remaining' },
    { value: 'completed_count', label: 'Completed Count' },
    { value: 'remaining_count', label: 'Remaining Count' },
    { value: 'status', label: 'Status' },
    { value: 'delivery_appt', label: 'Delivery Appt' },
    { value: 'dispatcher', label: 'Dispatcher' },
  ];

  // UI state
  const [sortBy, setSortBy] = useState<DriverSortOption>('unit_number');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [showCompleted, setShowCompleted] = useState(false);
  const [searchTerm, setSearchTerm] = useState(() => searchParams.get('driver') || '');

  // Sync searchTerm when URL ?driver= param changes (e.g. clicking DSM from global search while already on this page)
  useEffect(() => {
    const driverParam = searchParams.get('driver') || '';
    if (driverParam && driverParam !== searchTerm) {
      setSearchTerm(driverParam);
    }
  }, [searchParams]);

  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['requesting', 'critical', 'atRisk', 'onTrack']));
  const [settingsDrawerOpen, setSettingsDrawerOpen] = useState(false);
  const [displaySettings, setDisplaySettings] = useState<DisplaySettings>(DEFAULT_DISPLAY_SETTINGS);
  const homeRegionInitialized = useRef(false);
  const displaySettingsLoaded = useRef(false);
  const fetchAbortControllerRef = useRef<AbortController | null>(null);

  // Filter bar wraps onto a second row when the viewport can't hold both
  // groups; track that so we can draw a divider under the first row only then.
  const filterGroupRef = useRef<HTMLDivElement>(null);
  const controlsGroupRef = useRef<HTMLDivElement>(null);
  const [filterBarWrapped, setFilterBarWrapped] = useState(false);
  useEffect(() => {
    const filterGroup = filterGroupRef.current;
    const controlsGroup = controlsGroupRef.current;
    if (!filterGroup || !controlsGroup) return;
    const measure = () => setFilterBarWrapped(controlsGroup.offsetTop > filterGroup.offsetTop);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(filterGroup);
    observer.observe(controlsGroup);
    return () => observer.disconnect();
  }, []);

  // Drag state
  const [dragState, setDragState] = useState<DragState | null>(null);
  const [dragTargetDriver, setDragTargetDriver] = useState<string | null>(null);

  // Reassignment state
  const [reassigning, setReassigning] = useState(false);
  const [reordering, setReordering] = useState(false);

  // Pending reorder state: tracks unsaved priority changes per driver.
  // pendingReordersRef mirrors state so async callers (refreshAll right after
  // Apply/Cancel) read the post-update value without waiting for a re-render.
  const [pendingReorders, setPendingReorders] = useState<Record<string, { pronumber: number; legnum: number; priority: number }[]>>({});
  const pendingReordersRef = useRef(pendingReorders);
  const updatePendingReorders = useCallback(
    (updater: (prev: Record<string, { pronumber: number; legnum: number; priority: number }[]>) => Record<string, { pronumber: number; legnum: number; priority: number }[]>) => {
      const next = updater(pendingReordersRef.current);
      pendingReordersRef.current = next;
      setPendingReorders(next);
    },
    []
  );
  const hasPendingReorders = Object.keys(pendingReorders).length > 0;
  const pendingReorderDrivers = useMemo(() => new Set(Object.keys(pendingReorders)), [pendingReorders]);
  const pendingReorderUnits = useMemo(() => Object.keys(pendingReorders).sort(), [pendingReorders]);

  // Remove from driver modal state
  const [removeMove, setRemoveMove] = useState<MoveRisk | null>(null);
  const [removeGroup, setRemoveGroup] = useState<MoveGroup | null>(null);
  const [removing, setRemoving] = useState(false);
  const { checkAndPrompt, showNoteModal, handleNoteSubmit: onNoteSubmit, handleNoteCancel, isChecking: isCheckingDeadline } = useDeadlineCheck();

  // Order details drawer state
  const [isOrderDrawerOpen, setIsOrderDrawerOpen] = useState(false);
  const [selectedPro, setSelectedPro] = useState<number | null>(null);

  // Available moves drawer state
  const [availableMovesDrawerOpen, setAvailableMovesDrawerOpen] = useState(false);
  const [availableMovesDriverUnit, setAvailableMovesDriverUnit] = useState<string | null>(null);
  const [availableMovesDriverName, setAvailableMovesDriverName] = useState<string>('');
  const [availableMoves, setAvailableMoves] = useState<any[]>([]);
  const [availableMovesLoading, setAvailableMovesLoading] = useState(false);
  // A failed fetch must not masquerade as "no moves" — a dispatcher would
  // wrongly conclude there is no work to give the driver (2026-08-25 sweep).
  const [availableMovesError, setAvailableMovesError] = useState(false);
  const [availableMovesSearch, setAvailableMovesSearch] = useState('');
  const [assigningPro, setAssigningPro] = useState<number | null>(null);
  const [visibleAvailableMoves, setVisibleAvailableMoves] = useState<any[]>([]);
  const [drawerMapBounds, setDrawerMapBounds] = useState<google.maps.LatLngBounds | null>(null);
  const [selectedMapPros, setSelectedMapPros] = useState<number[]>([]);
  const [drawerMapData, setDrawerMapData] = useState<{
    fromName: string;
    fromCityState: string;
    fromLat: number;
    fromLng: number;
    toName: string;
    toCityState: string;
    toLat: number;
    toLng: number;
    travelMinutes: number;
  } | null>(null);

  // Deadhead map modal state
  const [deadheadMapOpen, setDeadheadMapOpen] = useState(false);
  const [deadheadMapData, setDeadheadMapData] = useState<{
    fromName: string;
    fromCityState: string;
    fromLat: number;
    fromLng: number;
    toName: string;
    toCityState: string;
    toLat: number;
    toLng: number;
    travelMinutes: number;
    driverUnit: string;
    driverName: string;
  } | null>(null);

  // Ghost card modal state
  const [ghostModalData, setGhostModalData] = useState<{
    suggestion: GhostSuggestion;
    driverName: string;
    driverUnit: string;
  } | null>(null);

  // Driver note modal (the lane header's comment icon) state
  const [commentModal, setCommentModal] = useState<{
    unitnumber: string;
    driverName: string;
    comment: string | null;
  } | null>(null);
  const [commentSaving, setCommentSaving] = useState(false);

  // Google Maps loader
  const { isLoaded: isMapLoaded } = useJsApiLoader(getGoogleMapsLoaderOptions());

  // Fetch available moves for a driver
  const fetchAvailableMoves = useCallback(async (unitNumber: string) => {
    setAvailableMovesLoading(true);
    setAvailableMovesError(false);
    try {
      const response = await api.get('/orders/available-pros', {
        params: { unit_number: unitNumber }
      });
      if (response.data?.data && Array.isArray(response.data.data)) {
        setAvailableMoves(response.data.data);
      } else {
        setAvailableMoves([]);
      }
    } catch (error) {
      console.error('Error fetching available moves:', error);
      setAvailableMoves([]);
      setAvailableMovesError(true);
    } finally {
      setAvailableMovesLoading(false);
    }
  }, []);

  // Open available moves drawer
  const openAvailableMovesDrawer = (
    unitNumber: string,
    driverName: string,
    mapData?: {
      fromName: string;
      fromCityState: string;
      fromLat: number;
      fromLng: number;
      toName: string;
      toCityState: string;
      toLat: number;
      toLng: number;
      travelMinutes: number;
    }
  ) => {
    setAvailableMovesDriverUnit(unitNumber);
    setAvailableMovesDriverName(driverName);
    setDrawerMapData(mapData || null);
    setAvailableMovesDrawerOpen(true);
    setAvailableMovesSearch('');
    fetchAvailableMoves(unitNumber);
  };

  // Close available moves drawer
  const closeAvailableMovesDrawer = () => {
    setAvailableMovesDrawerOpen(false);
    setAvailableMovesDriverUnit(null);
    setAvailableMovesDriverName('');
    setAvailableMoves([]);
    setAvailableMovesSearch('');
    setAssigningPro(null);
    setDrawerMapData(null);
    setDrawerMapBounds(null);
    setVisibleAvailableMoves([]);
    setSelectedMapPros([]);
  };

  // Open deadhead map modal
  const openDeadheadMap = (
    prevMove: MoveRisk,
    nextMove: MoveRisk,
    travelMinutes: number,
    driverUnit: string,
    driverName: string
  ) => {
    if (prevMove.dest_lat && prevMove.dest_lng && nextMove.origin_lat && nextMove.origin_lng) {
      setDeadheadMapData({
        fromName: prevMove.dest_name,
        fromCityState: prevMove.dest_city_state,
        fromLat: prevMove.dest_lat,
        fromLng: prevMove.dest_lng,
        toName: nextMove.origin_name,
        toCityState: nextMove.origin_city_state,
        toLat: nextMove.origin_lat,
        toLng: nextMove.origin_lng,
        travelMinutes,
        driverUnit,
        driverName,
      });
      setDeadheadMapOpen(true);
    }
  };

  // Assign moves to the current driver
  const assignMoveToDriver = async (pros: any | any[]) => {
    if (!availableMovesDriverUnit || assigningPro) return;

    const prosArray = Array.isArray(pros) ? pros : [pros];
    if (prosArray.length === 0) return;

    const pronumber = prosArray[0].pronumber;
    setAssigningPro(pronumber);

    try {
      // Local date, not UTC: toISOString() rolls over to tomorrow after ~18:00
      // Central and would stamp the wrong appointment date. The backend applies
      // the same default via db_today() for the ghost-assign path.
      const today = toLocalDateKey();

      const legData = prosArray.map((pro: any) => {
        const origAppt = pro._cmp_origapptdate && pro._cmp_origapptdate !== '0000-00-00'
          ? pro._cmp_origapptdate : today;
        const destAppt = pro._cmp_destapptdate && pro._cmp_destapptdate !== '0000-00-00'
          ? pro._cmp_destapptdate : today;

        return {
          legnum: pro.legnum,
          origcode: pro.originCode,
          destcode: pro.destCode,
          driver: availableMovesDriverUnit,
          origApptDate: origAppt,
          destApptDate: destAppt,
          sendToGwiMove,
          old: {
            origcode: pro.originCode,
            destcode: pro.destCode,
            driver: '',
            origApptDate: pro._cmp_origapptdate || '',
            destApptDate: pro._cmp_destapptdate || '',
          },
        };
      });

      await ordersService.updateLegDetails(pronumber, legData);

      const legCount = prosArray.length;
      showToast({
        message: `Assigned PRO ${pronumber}${legCount > 1 ? ` (${legCount} legs)` : ''} to ${availableMovesDriverUnit}`,
        type: 'success'
      });
      closeAvailableMovesDrawer();

      refreshAll();
    } catch (error: any) {
      console.error('Error assigning move:', error);
      showToast({ message: error.response?.data?.detail || 'Failed to assign move', type: 'error' });
    } finally {
      setAssigningPro(null);
    }
  };

  // Helper to parse coordinates
  const parseCoordinates = useCallback((coords: string | null): { lat: number; lng: number } | null => {
    if (!coords) return null;
    const firstCoordPair = coords.split(/[\r\n;]+/)[0].trim();
    const parts = firstCoordPair.split(',').map(s => parseFloat(s.trim()));
    if (parts.length === 2 && !isNaN(parts[0]) && !isNaN(parts[1])) {
      if (Math.abs(parts[0]) > 90 && Math.abs(parts[1]) < 90) {
        return { lat: parts[1], lng: parts[0] };
      }
      return { lat: parts[0], lng: parts[1] };
    }
    return null;
  }, []);

  // Filter available moves
  const filteredAvailableMoves = useMemo(() => {
    return availableMoves.filter(pro => {
      if (availableMovesSearch.trim()) {
        const search = availableMovesSearch.toLowerCase();
        const matchesSearch = (
          String(pro.pronumber).toLowerCase().includes(search) ||
          (pro.customerName || '').toLowerCase().includes(search) ||
          (pro.originName || '').toLowerCase().includes(search) ||
          (pro.destName || '').toLowerCase().includes(search)
        );
        if (!matchesSearch) return false;
      }

      if (selectedMapPros.length > 0) {
        return selectedMapPros.includes(pro.pronumber);
      }

      if (drawerMapBounds && pro.originCoordinates) {
        const coords = parseCoordinates(pro.originCoordinates);
        if (coords) {
          return drawerMapBounds.contains(coords);
        }
        return false;
      }

      return true;
    });
  }, [availableMoves, availableMovesSearch, selectedMapPros, drawerMapBounds, parseCoordinates]);

  // Group linked moves by pronumber
  const groupedAvailableMoves = filteredAvailableMoves.reduce<any[][]>((acc, pro) => {
    const existingGroup = acc.find((g: any[]) => g[0].pronumber === pro.pronumber);
    if (existingGroup) {
      existingGroup.push(pro);
    } else {
      acc.push([pro]);
    }
    return acc;
  }, []);

  // Auto-select user's home region
  useEffect(() => {
    if (
      !homeRegionInitialized.current &&
      !globalFiltersLoading &&
      selectedRegions.length === 0 &&
      permissions?.home_region &&
      permissions?.home_region_as_default !== false &&
      regionOptions.length > 0 &&
      regionOptions.some(opt => opt.value === permissions.home_region)
    ) {
      setSelectedRegions([permissions.home_region]);
      homeRegionInitialized.current = true;
    }
  }, [permissions?.home_region, permissions?.home_region_as_default, regionOptions, globalFiltersLoading, selectedRegions.length, setSelectedRegions]);

  // Clear all server-side filters (used when a driver arrived via ?driver= is outside current filters)
  const handleClearServerFilters = useCallback(() => {
    setSelectedRegions([]);
    setSelectedDriverDispatchers([]);
    setSelectedOrderDispatchers([]);
    // Prevent the home-region auto-select from re-applying after we clear
    homeRegionInitialized.current = true;
  }, [setSelectedRegions, setSelectedDriverDispatchers, setSelectedOrderDispatchers]);

  // Fetch filter options
  const fetchFilterOptions = useCallback(async () => {
    try {
      const response = await api.get('/driver-status-monitor/filter-options');
      const { regions } = response.data;
      setRegionOptions(regions.map((r: string) => ({ value: r, label: r })));
    } catch (err) {
      console.error('Failed to fetch filter options:', err);
    }
  }, []);

  // Fetch driver data
  const fetchDrivers = useCallback(async (isBackgroundRefresh = false) => {
    if (fetchAbortControllerRef.current) {
      fetchAbortControllerRef.current.abort();
    }

    const controller = new AbortController();
    fetchAbortControllerRef.current = controller;

    if (!isBackgroundRefresh) {
      setLoading(true);
      setLoadStartTime(Date.now());
      setElapsedSeconds(0);
    }
    setError(null);

    try {
      const params = new URLSearchParams();
      if (selectedOrderDispatchers.length > 0) {
        params.append('dispatcher', selectedOrderDispatchers.join(','));
      }
      if (selectedDriverDispatchers.length > 0) {
        params.append('driver_dispatcher', selectedDriverDispatchers.join(','));
      }
      if (selectedRegions.length > 0) {
        params.append('region', selectedRegions.join(','));
      }
      params.append('start_date', dateRange.start_date);
      params.append('end_date', dateRange.end_date);

      const response = await api.get(`/driver-status-monitor/drivers?${params.toString()}`, {
        signal: controller.signal
      });
      const newData: DriverRiskData = response.data;
      const lockedUnitsArr = Object.keys(pendingReordersRef.current);

      if (lockedUnitsArr.length === 0) {
        setData(newData);
      } else {
        // Locked drivers (those with unsaved priority changes) keep their previous
        // bucket placement and move data through this refresh. Other drivers update
        // normally. If a locked driver vanished from the new snapshot entirely, their
        // pending edits are no longer meaningful — auto-cancel and toast.
        const newDataUnits = new Set<string>();
        for (const d of newData.critical_drivers) newDataUnits.add(d.unitnumber);
        for (const d of newData.at_risk_drivers) newDataUnits.add(d.unitnumber);
        for (const d of newData.on_track_drivers) newDataUnits.add(d.unitnumber);
        for (const d of newData.completed_drivers) newDataUnits.add(d.unitnumber);

        const disappeared = lockedUnitsArr.filter(u => !newDataUnits.has(u));
        if (disappeared.length > 0) {
          updatePendingReorders(prev => {
            const next = { ...prev };
            for (const u of disappeared) delete next[u];
            return next;
          });
          for (const u of disappeared) {
            showToast({
              message: `Driver ${u} is no longer in your view; pending priority changes were discarded.`,
              type: 'info',
              duration: 6000,
            });
          }
        }

        const stillLocked = new Set(lockedUnitsArr.filter(u => newDataUnits.has(u)));
        setData(prevData => {
          if (!prevData || stillLocked.size === 0) return newData;
          const buckets = ['critical_drivers', 'at_risk_drivers', 'on_track_drivers', 'completed_drivers'] as const;
          const lockedFromPrev = new Map<string, { driver: DriverWithMoves; bucket: typeof buckets[number] }>();
          for (const bucket of buckets) {
            for (const driver of prevData[bucket]) {
              if (stillLocked.has(driver.unitnumber)) {
                lockedFromPrev.set(driver.unitnumber, { driver, bucket });
              }
            }
          }
          const result: DriverRiskData = { ...newData };
          for (const bucket of buckets) {
            result[bucket] = newData[bucket].filter(d => !stillLocked.has(d.unitnumber));
          }
          for (const { driver, bucket } of lockedFromPrev.values()) {
            result[bucket] = [...result[bucket], driver];
          }
          return result;
        });
      }
    } catch (err: any) {
      if (err.name === 'AbortError' || err.code === 'ERR_CANCELED') {
        return;
      }
      setError(err.response?.data?.detail || 'Failed to fetch driver data');
    } finally {
      setLoading(false);
      setLoadStartTime(null);
    }
  }, [selectedOrderDispatchers, selectedDriverDispatchers, selectedRegions, dateRange, showToast, updatePendingReorders]);

  // Fetch dispatch requests (drivers requesting more work).
  // Per DFW: this section stays fleet-wide and intentionally ignores the
  // driver/order dispatcher filters so dispatchers can see every requester
  // for FIFO triage. Only the region filter is applied.
  const fetchDispatchRequests = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (selectedRegions.length > 0) {
        params.append('region', selectedRegions.join(','));
      }
      const qs = params.toString();
      const response = await api.get(`/driver-status-monitor/dispatch-requests${qs ? `?${qs}` : ''}`);
      const requests: Array<{ recno: number; unitnumber: string; createdatetime: string; driver_dispatcher?: string }> = response.data.requests || [];
      const lanes: DriverWithMoves[] = response.data.lanes || [];
      dispatchRequestsRef.current = requests;

      // Update the set of unitnumbers with active requests
      setDispatchRequestUnitnumbers(new Set(requests.map(r => r.unitnumber)));
      setDispatchRequestCreatedAt(new Map(requests.map(r => [r.unitnumber, r.createdatetime])));
      setRequestingDrivers(lanes);

      // Toasts are gated by the "Show Toast For Driver Requesting Work" preference.
      if (!showDriverRequestToast) return;

      // When "Show Only My Drivers" is on, filter to requests where the driver's
      // dispatcher matches the signed-in user's V1 username (case-insensitive).
      const myV1Username = (permissions?.v1_username || '').toLowerCase();
      const toastRequests = showOnlyMyDrivers
        ? requests.filter(r => (r.driver_dispatcher || '').toLowerCase() === myV1Username && myV1Username !== '')
        : requests;

      console.log('[DSM toast filter]', {
        showOnlyMyDrivers,
        myV1Username,
        rawV1Username: permissions?.v1_username,
        totalRequests: requests.length,
        afterFilter: toastRequests.length,
        requests: requests.map(r => ({
          recno: r.recno,
          unitnumber: r.unitnumber,
          driver_dispatcher: r.driver_dispatcher,
          driver_dispatcher_lower: (r.driver_dispatcher || '').toLowerCase(),
          matches: (r.driver_dispatcher || '').toLowerCase() === myV1Username && myV1Username !== '',
        })),
      });

      const dismissedKey = 'dsm-dispatch-requests-dismissed';
      const dismissed: number[] = JSON.parse(localStorage.getItem(dismissedKey) || '[]');
      const dismissedSet = new Set(dismissed);

      for (const req of toastRequests) {
        // Skip if already dismissed in localStorage or already toasted this session
        if (dismissedSet.has(req.recno) || shownToastRecnosRef.current.has(req.recno)) continue;

        shownToastRecnosRef.current.add(req.recno);

        const toastId = showToast({
          message: `Driver ${req.unitnumber} requested more work`,
          type: 'info',
          duration: 0, // persistent until dismissed
          onDismiss: () => {
            // Persist dismissal in localStorage
            const current: number[] = JSON.parse(localStorage.getItem(dismissedKey) || '[]');
            if (!current.includes(req.recno)) {
              current.push(req.recno);
              localStorage.setItem(dismissedKey, JSON.stringify(current));
            }
            requestToastIdsRef.current.delete(req.recno);
          },
        });
        requestToastIdsRef.current.set(req.recno, toastId);
      }
    } catch (err) {
      console.error('Failed to fetch dispatch requests:', err);
    }
  }, [showToast, selectedRegions, showDriverRequestToast, showOnlyMyDrivers, permissions?.v1_username]);

  // Refresh both driver lanes and the requesting-work bucket together so a
  // dispatch action that clears legdispatchrequest in legacy is reflected in
  // the "Requesting Work" section and hand-icon overlays at the same time as
  // the new moves appear in the driver's lane.
  const refreshAll = useCallback(async (isBackgroundRefresh = false) => {
    await Promise.all([fetchDrivers(isBackgroundRefresh), fetchDispatchRequests()]);
  }, [fetchDrivers, fetchDispatchRequests]);

  // Acknowledge a driver's work request when we have nothing to give them.
  // Clears the request in legacy (deleted='Y', logged to tablemaint_history as
  // an ack rather than a dispatch) so the lane leaves the board.
  const handleAcknowledgeRequest = useCallback(async (unitnumber: string) => {
    const snapshot = {
      units: dispatchRequestUnitnumbers,
      createdAt: dispatchRequestCreatedAt,
      lanes: requestingDrivers,
    };
    // Drop the lane on click rather than on the next 30s poll.
    const next = clearRequestingUnit(snapshot, unitnumber);
    setDispatchRequestUnitnumbers(next.units);
    setDispatchRequestCreatedAt(next.createdAt);
    setRequestingDrivers(next.lanes);

    try {
      await api.post('/driver-status-monitor/acknowledge-request', { unitnumber });

      // Close the now-obsolete persistent toasts for this driver.
      for (const toastId of toastIdsForUnit(dispatchRequestsRef.current, requestToastIdsRef.current, unitnumber)) {
        dismissToast(toastId);
      }

      showToast({ message: `Driver ${unitnumber} acknowledged - no work available`, type: 'success' });
      await refreshAll(true);
    } catch (err: any) {
      // Put the driver back -- silently hiding someone who is still waiting for
      // work is worse than a lane that lingers.
      setDispatchRequestUnitnumbers(snapshot.units);
      setDispatchRequestCreatedAt(snapshot.createdAt);
      setRequestingDrivers(snapshot.lanes);

      const detail = err?.response?.data?.detail;
      const alreadyCleared = err?.response?.status === 404;
      showToast({
        message: alreadyCleared
          ? `Driver ${unitnumber}'s request was already cleared`
          : detail || `Failed to acknowledge driver ${unitnumber}`,
        type: alreadyCleared ? 'info' : 'error',
      });
      // A 404 means legacy already moved on; resync so the board agrees.
      if (alreadyCleared) await refreshAll(true);
    }
  }, [dispatchRequestUnitnumbers, dispatchRequestCreatedAt, requestingDrivers, dismissToast, showToast, refreshAll]);

  // Open the driver-note modal from a lane header's comment icon.
  const handleEditComment = useCallback((unitnumber: string, driverName: string, currentComment: string | null) => {
    setCommentModal({ unitnumber, driverName, comment: currentComment });
  }, []);

  // Save the note: today's driverstatus.comments, comment-only (the endpoint
  // never touches the driver's status/category). Patch every bucket the driver
  // appears in so the icon updates on click, then background-refresh so the
  // board agrees with the server and any in-flight poll can't flip it back.
  const handleSaveComment = useCallback(async (comment: string) => {
    if (!commentModal) return;
    const { unitnumber } = commentModal;
    setCommentSaving(true);
    try {
      const response = await api.post('/driver-status-monitor/driver-comment', { unitnumber, comment });
      const saved: string | null = response.data?.comment ?? null;

      const patch = (d: DriverWithMoves): DriverWithMoves =>
        d.unitnumber === unitnumber ? { ...d, today_comment: saved } : d;
      setData(prev => prev ? {
        ...prev,
        critical_drivers: prev.critical_drivers.map(patch),
        at_risk_drivers: prev.at_risk_drivers.map(patch),
        on_track_drivers: prev.on_track_drivers.map(patch),
        completed_drivers: prev.completed_drivers.map(patch),
      } : prev);
      setRequestingDrivers(prev => prev.map(patch));

      setCommentModal(null);
      showToast({
        message: saved ? `Note saved for driver ${unitnumber}` : `Note cleared for driver ${unitnumber}`,
        type: 'success',
      });
      await refreshAll(true);
    } catch (err: any) {
      const forbidden = err?.response?.status === 403;
      showToast({
        message: forbidden
          ? "You don't have permission to edit driver notes"
          : err?.response?.data?.detail || `Failed to save note for driver ${unitnumber}`,
        type: 'error',
      });
    } finally {
      setCommentSaving(false);
    }
  }, [commentModal, showToast, refreshAll]);

  // Load display settings
  useEffect(() => {
    const loadDisplaySettings = async () => {
      try {
        const setting = await userSettingsService.getUserSetting(DISPLAY_SETTINGS_KEY);
        if (setting?.setting_value) {
          setDisplaySettings({ ...DEFAULT_DISPLAY_SETTINGS, ...setting.setting_value });
        }
      } catch (err) {
        console.error('Failed to load display settings:', err);
      } finally {
        displaySettingsLoaded.current = true;
      }
    };
    loadDisplaySettings();
  }, []);

  // Save display settings (debounced)
  const displaySettingsSaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  useEffect(() => {
    if (!displaySettingsLoaded.current) return;

    if (displaySettingsSaveTimeoutRef.current) {
      clearTimeout(displaySettingsSaveTimeoutRef.current);
    }
    displaySettingsSaveTimeoutRef.current = setTimeout(() => {
      userSettingsService.saveUserSetting(DISPLAY_SETTINGS_KEY, displaySettings)
        .catch(err => console.error('Failed to save display settings:', err));
    }, 500);

    return () => {
      if (displaySettingsSaveTimeoutRef.current) {
        clearTimeout(displaySettingsSaveTimeoutRef.current);
      }
    };
  }, [displaySettings]);

  // Initial load
  useEffect(() => {
    fetchFilterOptions();
  }, []);

  // Refetch when filters change. Gated on the global-filters restore: without
  // the gate the mount fired an UNFILTERED all-region /drivers (the most
  // expensive shape of that endpoint) that the saved-filter restore then
  // aborted and refired — 2-3 full fetches per page load (2026-08-25 sweep).
  useEffect(() => {
    if (globalFiltersLoading) return;
    refreshAll();
  }, [globalFiltersLoading, selectedOrderDispatchers, selectedDriverDispatchers, selectedRegions, dateRange]);

  // Auto-refresh. Locked drivers (with pending priority edits) are held back
  // from refresh-merges in fetchDrivers itself, so the interval can run freely.
  useVisibilityAwareInterval(() => refreshAll(true), REFRESH_INTERVAL);

  // Browser-level guard: warn before tab close / refresh / external nav while
  // priority edits are pending. The dialog text is browser-controlled.
  useEffect(() => {
    if (!hasPendingReorders) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [hasPendingReorders]);

  // Update elapsed timer while loading
  useEffect(() => {
    if (!loadStartTime) return;

    const interval = setInterval(() => {
      setElapsedSeconds(Math.floor((Date.now() - loadStartTime) / 1000));
    }, 1000);

    return () => clearInterval(interval);
  }, [loadStartTime]);

  // Update footer with active users display
  useEffect(() => {
    const currentUserId = activeUsers.find(user => user.sid === socket?.id)?.user_id;
    const seen = new Set<string>();
    const otherUsers = activeUsers.filter(user => {
      if (user.user_id === currentUserId || seen.has(user.user_id)) {
        return false;
      }
      seen.add(user.user_id);
      return true;
    });
    const maxVisible = 10;
    const visibleUsers = otherUsers.slice(0, maxVisible);
    const overflowUsers = otherUsers.slice(maxVisible);

    const getInitials = (name: string): string => {
      return name
        .split(' ')
        .map(n => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
    };

    const getUserColor = (name: string): string => {
      const colors = [
        'bg-blue-500', 'bg-green-500', 'bg-yellow-500', 'bg-red-500',
        'bg-purple-500', 'bg-pink-500', 'bg-indigo-500', 'bg-teal-500',
      ];
      const hash = name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
      return colors[hash % colors.length];
    };

    setDispatchButton(
      <div className="flex items-center justify-between w-full">
        <div className="flex items-center gap-2">
          {otherUsers.length > 0 && (
            <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 dark:bg-gray-700 rounded-lg">
              <Users className="h-4 w-4 text-gray-600 dark:text-gray-300" />
              <div className="flex -space-x-1">
                {visibleUsers.map(user => (
                  <Tooltip key={user.user_id} content={user.user_name}>
                    <div
                      className={`inline-flex h-6 w-6 items-center justify-center rounded-full border-2 border-white dark:border-gray-700 text-[0.625rem] font-medium text-white cursor-pointer ${getUserColor(user.user_name)}`}
                    >
                      {getInitials(user.user_name)}
                    </div>
                  </Tooltip>
                ))}
                {overflowUsers.length > 0 && (
                  <Tooltip
                    content={
                      <div className="flex flex-col gap-1">
                        {overflowUsers.map(user => (
                          <span key={user.user_id}>{user.user_name}</span>
                        ))}
                      </div>
                    }
                  >
                    <div className="inline-flex h-6 w-6 items-center justify-center rounded-full border-2 border-white dark:border-gray-700 bg-gray-500 text-[0.625rem] font-medium text-white cursor-pointer">
                      +{overflowUsers.length}
                    </div>
                  </Tooltip>
                )}
              </div>
            </div>
          )}
        </div>
        <div className="flex-1" />
        {/* Per-page override of the user's defaultSendToGwiMove setting. When on,
            every driver assignment made on this page is also forwarded to
            /sendtogwimove after the CMS assignment itself succeeds. */}
        <label className="flex items-center gap-2 text-sm text-label cursor-pointer select-none">
          <Toggle
            checked={sendToGwiMove}
            onChange={setSendToGwiMove}
            size="sm"
          />
          Send to gwiMove
        </label>
      </div>
    );

    return () => {
      setDispatchButton(null);
    };
  }, [activeUsers, socket?.id, setDispatchButton, sendToGwiMove]);

  // Toggle section expanded/collapsed
  const toggleSection = useCallback((section: string) => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      if (next.has(section)) {
        next.delete(section);
      } else {
        next.add(section);
      }
      return next;
    });
  }, []);

  const scrollToSection = useCallback((sectionId: string, sectionKey: string) => {
    setExpandedSections(prev => {
      if (!prev.has(sectionKey)) {
        const next = new Set(prev);
        next.add(sectionKey);
        return next;
      }
      return prev;
    });
    setTimeout(() => {
      document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  }, []);

  // Find the driver's bucket in current data, expand that section if collapsed,
  // then scroll the lane into view. Used by the unsaved-edits banner chips.
  const scrollToDriver = useCallback((unitnumber: string) => {
    const sectionKeyByBucket: Array<[keyof DriverRiskData, string]> = [
      ['critical_drivers', 'critical'],
      ['at_risk_drivers', 'atRisk'],
      ['on_track_drivers', 'onTrack'],
      ['completed_drivers', 'completed'],
    ];
    let sectionKey: string | null = null;
    if (data) {
      for (const [bucket, key] of sectionKeyByBucket) {
        const list = data[bucket] as DriverWithMoves[] | undefined;
        if (Array.isArray(list) && list.some(d => d.unitnumber === unitnumber)) {
          sectionKey = key;
          break;
        }
      }
    }
    if (sectionKey) {
      setExpandedSections(prev => (prev.has(sectionKey!) ? prev : new Set(prev).add(sectionKey!)));
    }
    setTimeout(() => {
      document.querySelector(`[data-unitnumber="${unitnumber}"]`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 50);
  }, [data]);

  // Drag handlers
  const handleDragStart = useCallback((e: React.DragEvent, move: MoveRisk, sourceDriver: string, group?: MoveGroup) => {
    e.dataTransfer.effectAllowed = 'move';
    setDragState({ move, sourceDriver, group: group || null });
  }, []);

  const handleDragEnd = useCallback(() => {
    setDragState(null);
    setDragTargetDriver(null);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  }, []);

  const handleDrop = useCallback(async (e: React.DragEvent, targetDriver: string) => {
    e.preventDefault();

    if (!dragState || dragState.sourceDriver !== targetDriver) {
      handleDragEnd();
      return;
    }

    handleDragEnd();
  }, [dragState, handleDragEnd]);

  // Reorder handler - updates local state only (deferred save)
  const handleReorder = useCallback((driverUnitnumber: string, newOrder: MoveGroup[]) => {
    // Build priority assignments from the new order
    const moves: { pronumber: number; legnum: number; priority: number }[] = [];
    newOrder.forEach((group, groupIndex) => {
      group.moves.forEach(move => {
        moves.push({
          pronumber: move.pronumber,
          legnum: move.legnum,
          priority: groupIndex + 1,
        });
      });
    });

    // Store pending changes for this driver
    updatePendingReorders(prev => ({ ...prev, [driverUnitnumber]: moves }));

    // Update local data state so ETAs recalculate
    setData(prevData => {
      if (!prevData) return prevData;
      const priorityMap = new Map(moves.map(m => [`${m.pronumber}-${m.legnum}`, m.priority]));

      const updateDriverMoves = (drivers: DriverWithMoves[]) =>
        drivers.map(d => {
          if (d.unitnumber !== driverUnitnumber) return d;
          return {
            ...d,
            moves: d.moves.map(m => {
              const newPriority = priorityMap.get(`${m.pronumber}-${m.legnum}`);
              return newPriority !== undefined ? { ...m, dispatch_priority: newPriority } : m;
            }),
          };
        });

      return {
        ...prevData,
        critical_drivers: updateDriverMoves(prevData.critical_drivers),
        at_risk_drivers: updateDriverMoves(prevData.at_risk_drivers),
        on_track_drivers: updateDriverMoves(prevData.on_track_drivers),
        completed_drivers: updateDriverMoves(prevData.completed_drivers),
      };
    });

    handleDragEnd();
  }, [handleDragEnd]);

  // Apply pending reorder for a driver - sends to backend
  const handleApplyReorder = useCallback(async (driverUnitnumber: string) => {
    const moves = pendingReorders[driverUnitnumber];
    if (!moves) return;

    setReordering(true);
    try {
      const response = await api.post('/driver-status-monitor/reorder-moves', {
        driver_unitnumber: driverUnitnumber,
        moves,
      });

      if (response.data.success) {
        updatePendingReorders(prev => {
          const next = { ...prev };
          delete next[driverUnitnumber];
          return next;
        });
        await refreshAll(true);
      } else {
        // CMS returned 200 but rejected one or more legs (e.g. a move that has
        // already started, or a leg missing its chassis type). Those legs keep
        // their old priority. Surface the reason, then discard the pending edit
        // and re-read so the badges reflect what actually saved instead of the
        // optimistic order.
        const reason = response.data.message || 'One or more moves could not be reordered.';
        alert(`Some moves could not be reordered:\n${reason}`);
        updatePendingReorders(prev => {
          const next = { ...prev };
          delete next[driverUnitnumber];
          return next;
        });
        await refreshAll(true);
      }
    } catch (err: any) {
      const errorMsg = err.response?.data?.detail || 'Failed to reorder moves';
      alert(`Reorder failed: ${errorMsg}`);
    } finally {
      setReordering(false);
    }
  }, [pendingReorders, refreshAll, updatePendingReorders]);

  // Cancel pending reorder for a driver - refetch to get original data
  const handleCancelReorder = useCallback(async (driverUnitnumber: string) => {
    updatePendingReorders(prev => {
      const next = { ...prev };
      delete next[driverUnitnumber];
      return next;
    });
    await refreshAll(true);
  }, [refreshAll, updatePendingReorders]);

  // Remove from driver handler
  const handleRemove = useCallback((move: MoveRisk, group?: MoveGroup) => {
    setRemoveMove(move);
    setRemoveGroup(group || null);
  }, []);

  // Quick assign handler
  const handleQuickAssign = useCallback(async (move: MoveRisk, targetUnitnumber: string) => {
    setReassigning(true);
    try {
      const response = await api.post('/driver-status-monitor/reassign-move', {
        pronumber: move.pronumber,
        legnum: move.legnum,
        new_driver: targetUnitnumber,
        send_to_gwimove: sendToGwiMove,
      });

      if (response.data.success) {
        await refreshAll(true);
      } else {
        const errorMsg = response.data.message || 'Failed to reassign move';
        alert(`Reassign failed: ${errorMsg}`);
      }
    } catch (err: any) {
      const errorMsg = err.response?.data?.detail || 'Failed to reassign move';
      alert(`Reassign failed: ${errorMsg}`);
    } finally {
      setReassigning(false);
    }
  }, [refreshAll, sendToGwiMove]);

  // Ghost card click handler - opens confirmation modal
  const handleGhostCardClick = useCallback((suggestion: GhostSuggestion) => {
    // Find the driver that has this suggestion
    const allDrivers = [
      ...(data?.critical_drivers || []),
      ...(data?.at_risk_drivers || []),
      ...(data?.on_track_drivers || []),
      ...(data?.completed_drivers || []),
    ];
    const driver = allDrivers.find(d =>
      d.ghost_suggestions?.some(gs => gs.id === suggestion.id)
    );
    if (driver) {
      setGhostModalData({
        suggestion,
        driverName: driver.driver_name,
        driverUnit: driver.unitnumber,
      });
    }
  }, [data]);

  // Ghost card assign handler - assigns the move to driver
  const handleGhostAssign = useCallback(async () => {
    if (!ghostModalData) return;

    const { suggestion, driverUnit } = ghostModalData;
    try {
      const response = await api.post('/driver-status-monitor/assign-ghost', {
        suggestion_id: suggestion.id,
        send_to_gwimove: sendToGwiMove,
      });

      if (response.data.success) {
        showToast({
          message: `Assigned PRO ${suggestion.pronumber}-${suggestion.legnum} to ${driverUnit}`,
          type: 'success'
        });
        setGhostModalData(null);
        // Refresh driver data
        await refreshAll(true);
      } else {
        const errorMsg = response.data.message || 'Failed to assign move';
        showToast({ message: errorMsg, type: 'error' });
      }
    } catch (err: any) {
      // Check for 409 conflict (move already assigned to another driver)
      if (err.response?.status === 409) {
        showToast({ message: 'Move was already assigned to another driver', type: 'error' });
      } else {
        const errorMsg = err.response?.data?.detail || 'Failed to assign move';
        showToast({ message: errorMsg, type: 'error' });
      }
    }
  }, [ghostModalData, refreshAll, showToast, sendToGwiMove]);

  // Confirm removal
  const handleConfirmRemove = useCallback(async () => {
    if (!removeMove) return;

    const movesToRemove = removeGroup && removeGroup.isGroup
      ? removeGroup.moves
      : [removeMove];

    setRemoving(true);
    try {
      // Check deadline criteria before proceeding
      const deadlineCheck = await checkAndPrompt(
        movesToRemove.map(move => ({
          proNumber: move.pronumber,
          legnum: move.legnum,
          oldDriver: move.driver || '',
          newDriver: '',
        }))
      );

      if (deadlineCheck === null) {
        // User cancelled the note modal
        setRemoving(false);
        return;
      }

      const qualifyingSet = new Set(deadlineCheck.qualifyingPros);
      // Build enriched note: "Category - Reason - Notes" or "Category - Notes"
      const enrichedNote = deadlineCheck.note
        ? [deadlineCheck.category_name, deadlineCheck.reason_name, deadlineCheck.note].filter(Boolean).join(' - ')
        : null;
      const response = await api.post('/orders/remove-from-driver', {
        items: movesToRemove.map(move => ({
          proNumber: move.pronumber,
          legnum: move.legnum,
          origcode: move.origcode,
          destcode: move.destcode,
          origApptDate: move.origapptdate || '',
          destApptDate: move.destapptdate || '',
          driver: '',
          oldDriver: move.driver || '',
          ...(enrichedNote && qualifyingSet.has(move.pronumber) ? { notes: enrichedNote } : {}),
        })),
      });

      if (response.data.success > 0) {
        // Record dispatch removal audit entries
        if (deadlineCheck.note && deadlineCheck.qualifyingDetails.length > 0) {
          dispatchRemovalService.recordRemovals(
            deadlineCheck.qualifyingDetails.map(d => ({
              category_id: deadlineCheck.category_id!,
              reason_id: deadlineCheck.reason_id,
              notes: deadlineCheck.note!,
              pronumber: d.pronumber,
              driver: d.driver,
              dispatch_id: d.dispatch_id,
              appt_date: d.appt_date,
              deadline_type: d.deadline_type,
              deadline_date: d.deadline_date,
            }))
          ).catch(err => console.error('Failed to record dispatch removals:', err));
        }
        await refreshAll(true);
        setRemoveMove(null);
        setRemoveGroup(null);
      } else {
        const errorMsg = response.data.errors?.[0] || 'Failed to remove from driver';
        alert(`Remove failed: ${errorMsg}`);
      }
    } catch (err: any) {
      const errorMsg = err.response?.data?.detail || 'Failed to remove from driver';
      alert(`Remove failed: ${errorMsg}`);
    } finally {
      setRemoving(false);
    }
  }, [removeMove, removeGroup, refreshAll, checkAndPrompt]);

  // Open order details drawer
  const openOrderDrawer = useCallback((pronumber: number) => {
    setSelectedPro(pronumber);
    setIsOrderDrawerOpen(true);
  }, []);

  // Close order details drawer
  const closeOrderDrawer = useCallback(() => {
    setIsOrderDrawerOpen(false);
    setSelectedPro(null);
  }, []);

  // Helper to determine driver's current status
  const getDriverStatus = useCallback((driver: DriverWithMoves): 'not_started' | 'between_moves' | 'in_progress' => {
    const incompleteMoves = driver.moves.filter(m => m.status !== 'Completed');
    const completedMoves = driver.moves.filter(m => m.status === 'Completed');
    const activeMoves = incompleteMoves.filter(m =>
      m.status === 'In Transit' || m.status === 'At Origin' || m.status === 'At Destination' || m.status === 'Started'
    );

    if (activeMoves.length > 0) return 'in_progress';
    if (completedMoves.length > 0 && incompleteMoves.every(m => m.status === 'Dispatched')) return 'between_moves';
    return 'not_started';
  }, []);

  // Filter drivers
  const filterDrivers = useCallback((drivers: DriverWithMoves[]): DriverWithMoves[] => {
    let filtered = drivers.filter(driver => driverMatchesTruckTypes(driver.preference, selectedTruckTypes));

    if (selectedStatuses.length > 0) {
      filtered = filtered.filter(driver => {
        const driverStatus = getDriverStatus(driver);
        return selectedStatuses.includes(driverStatus);
      });
    }

    if (selectedDutyStatuses.length > 0) {
      filtered = filtered.filter(driver => driverMatchesDutyStatuses(driver.current_duty_status, selectedDutyStatuses));
    }

    if (selectedSizes.length > 0) {
      filtered = filtered.filter(driver => driverMatchesSizes(driver.moves, selectedSizes));
    }

    if (selectedChassisTypes.length > 0) {
      filtered = filtered.filter(driver => driverMatchesChassisTypes(driver.moves, selectedChassisTypes));
    }

    if (showOnlyRequests) {
      filtered = filtered.filter(driver => dispatchRequestUnitnumbers.has(driver.unitnumber));
    }

    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase().trim();
      filtered = filtered.filter(driver => {
        // Exact match for unit number
        if (driver.unitnumber.toLowerCase() === term) return true;
        // Partial match for driver name
        if (driver.driver_name.toLowerCase().includes(term)) return true;
        // Partial match for truck license plate
        if (driver.truck_license_plate?.toLowerCase().includes(term)) return true;
        return driver.moves.some(move => {
          // Exact match for PRO number
          if (move.pronumber.toString() === term) return true;
          // Partial match for locations and customer
          if (move.origin_name?.toLowerCase().includes(term)) return true;
          if (move.dest_name?.toLowerCase().includes(term)) return true;
          if (move.origin_city_state?.toLowerCase().includes(term)) return true;
          if (move.dest_city_state?.toLowerCase().includes(term)) return true;
          if (move.customer_name?.toLowerCase().includes(term)) return true;
          return false;
        });
      });
    }

    return filtered;
  }, [searchTerm, selectedTruckTypes, selectedStatuses, selectedDutyStatuses, selectedSizes, selectedChassisTypes, showOnlyRequests, dispatchRequestUnitnumbers, getDriverStatus]);

  // Filtered driver lists
  const filteredData = useMemo(() => {
    if (!data) return null;

    const getStatusPriority = (driver: DriverWithMoves): number => {
      const activeMoves = driver.moves.filter(m =>
        m.status === 'In Transit' || m.status === 'At Origin' || m.status === 'At Destination'
      );
      if (activeMoves.some(m => m.status === 'In Transit')) return 1;
      if (activeMoves.some(m => m.status === 'At Destination')) return 2;
      if (activeMoves.some(m => m.status === 'At Origin')) return 3;
      if (driver.moves.some(m => m.status === 'Started')) return 4;
      if (driver.moves.some(m => m.status === 'Dispatched')) return 5;
      return 6;
    };

    const todayStr = new Date().toLocaleDateString('en-CA');
    const sortDrivers = (a: DriverWithMoves, b: DriverWithMoves): number => {
      let result: number;
      switch (sortBy) {
        case 'unit_number':
          result = a.unitnumber.localeCompare(b.unitnumber, undefined, { numeric: true });
          break;
        case 'start_time': {
          // Samsara on-duty start for the day. Drivers with no start time sort
          // last in BOTH directions, like delivery_appt.
          const aStart = a.driver_start_at || '';
          const bStart = b.driver_start_at || '';
          if (!aStart && bStart) return 1;
          if (aStart && !bStart) return -1;
          result = compareByStartTime(a, b);
          break;
        }
        case 'hos_remaining': {
          const aHos = a.drive_time_remaining_mins;
          const bHos = b.drive_time_remaining_mins;
          if (aHos === null && bHos === null) result = 0;
          else if (aHos === null) result = 1;
          else if (bHos === null) result = -1;
          else result = aHos - bHos;
          break;
        }
        case 'completed_count': {
          const aCompleted = a.moves.filter(m => m.status === 'Completed').length;
          const bCompleted = b.moves.filter(m => m.status === 'Completed').length;
          result = aCompleted - bCompleted;
          break;
        }
        case 'remaining_count': {
          const aRemaining = a.moves.filter(m => m.status !== 'Completed').length;
          const bRemaining = b.moves.filter(m => m.status !== 'Completed').length;
          result = aRemaining - bRemaining;
          break;
        }
        case 'status': {
          result = getStatusPriority(a) - getStatusPriority(b);
          break;
        }
        case 'delivery_appt': {
          const aAppt = nextDeliveryAppt(a, todayStr);
          const bAppt = nextDeliveryAppt(b, todayStr);
          // Drivers with no upcoming delivery appt sort last in BOTH directions.
          if (!aAppt && !bAppt) { result = 0; break; }
          if (!aAppt) return 1;
          if (!bAppt) return -1;
          if (aAppt.date !== bAppt.date) result = aAppt.date < bAppt.date ? -1 : 1;
          else result = aAppt.minutes - bAppt.minutes;
          break;
        }
        case 'dispatcher': {
          // Unassigned drivers sort last in BOTH directions, like delivery_appt.
          const aDisp = (a.driver_dispatcher || '').trim();
          const bDisp = (b.driver_dispatcher || '').trim();
          if (!aDisp && bDisp) return 1;
          if (aDisp && !bDisp) return -1;
          result = compareByDispatcher(a, b);
          break;
        }
        default:
          result = a.unitnumber.localeCompare(b.unitnumber, undefined, { numeric: true });
      }
      return sortDirection === 'desc' ? -result : result;
    };

    // Requesting-work lane: only on today's view. Sourced from /dispatch-requests
    // (fleet-wide, region-only filtering) so dispatcher filters don't hide
    // requesters from FIFO triage. For drivers who ALSO appear in a risk
    // bucket, swap in the rich record (with full move data) but keep the
    // request metadata (timestamp + formatted location) from the lane.
    // Drivers outside the user's dispatcher filter only have lane data and
    // render as a compact request-only card.
    const viewingToday = selectedDate === new Date().toLocaleDateString('en-CA');
    const riskBucketByUnit = new Map<string, DriverWithMoves>();
    for (const d of data.critical_drivers) riskBucketByUnit.set(d.unitnumber, d);
    for (const d of data.at_risk_drivers) riskBucketByUnit.set(d.unitnumber, d);
    for (const d of data.on_track_drivers) riskBucketByUnit.set(d.unitnumber, d);
    for (const d of data.completed_drivers) riskBucketByUnit.set(d.unitnumber, d);
    const term = searchTerm.toLowerCase().trim();
    const matchesRequestingFilters = (d: DriverWithMoves): boolean => {
      if (!dispatchRequestUnitnumbers.has(d.unitnumber)) return false;
      if (!driverMatchesTruckTypes(d.preference, selectedTruckTypes)) return false;
      if (term) {
        if (d.unitnumber.toLowerCase() === term) return true;
        if (d.driver_name.toLowerCase().includes(term)) return true;
        if (d.truck_license_plate?.toLowerCase().includes(term)) return true;
        return false;
      }
      return true;
    };
    const mergeWithRiskBucket = (lane: DriverWithMoves): DriverWithMoves => {
      const rich = riskBucketByUnit.get(lane.unitnumber);
      // The lane carries the driver's full fleet-wide move set (from
      // /dispatch-requests), so a requester's completed work always shows
      // regardless of the dispatcher filter. Prefer the lane's moves at full
      // brightness; the risk-bucket record (when present) only supplies the
      // richer driver-level header fields (HOS/utilization/etc.).
      if (!rich) return lane;
      const laneHasMoves = lane.moves && lane.moves.length > 0;
      return {
        ...rich,
        moves: laneHasMoves ? lane.moves : rich.moves,
        total_moves: laneHasMoves ? lane.total_moves : rich.total_moves,
        formatted_location: lane.formatted_location ?? rich.formatted_location,
        request_createdatetime: lane.request_createdatetime ?? rich.request_createdatetime,
        request_recno: lane.request_recno ?? rich.request_recno,
      };
    };
    // FIFO: oldest request first so dispatchers triage the driver who's been
    // waiting longest. Missing timestamp (shouldn't happen) sorts last.
    const sortByRequestAgeAsc = (a: DriverWithMoves, b: DriverWithMoves): number => {
      const aTs = a.request_createdatetime || dispatchRequestCreatedAt.get(a.unitnumber);
      const bTs = b.request_createdatetime || dispatchRequestCreatedAt.get(b.unitnumber);
      if (!aTs && !bTs) return a.unitnumber.localeCompare(b.unitnumber, undefined, { numeric: true });
      if (!aTs) return 1;
      if (!bTs) return -1;
      return aTs.localeCompare(bTs);
    };
    const requesting = viewingToday
      ? requestingDrivers
          .filter(matchesRequestingFilters)
          .map(mergeWithRiskBucket)
          .sort(sortByRequestAgeAsc)
      : [];

    return {
      requesting_drivers: requesting,
      critical_drivers: filterDrivers(data.critical_drivers).sort(sortDrivers),
      at_risk_drivers: filterDrivers(data.at_risk_drivers).sort(sortDrivers),
      on_track_drivers: filterDrivers(data.on_track_drivers).sort(sortDrivers),
      completed_drivers: filterDrivers(data.completed_drivers).sort(sortDrivers),
    };
  }, [data, filterDrivers, sortBy, sortDirection, requestingDrivers, selectedDate, dispatchRequestUnitnumbers, dispatchRequestCreatedAt, searchTerm, selectedTruckTypes]);

  const stats = useMemo(() => {
    if (!data) {
      return {
        critical: 0, atRisk: 0, onTrack: 0, completed: 0, total: 0, working: 0, workingPct: 0,
        dayStart: { started: 0, onDuty: 0, expected: 0 },
      };
    }
    const allBucketDrivers = [
      ...data.critical_drivers,
      ...data.at_risk_drivers,
      ...data.on_track_drivers,
      ...data.completed_drivers,
    ];
    const working = allBucketDrivers.filter(driver => getDriverStatus(driver) === 'in_progress').length;
    return {
      critical: data.critical_drivers.length,
      atRisk: data.at_risk_drivers.length,
      onTrack: data.on_track_drivers.length,
      completed: data.completed_drivers.length,
      total: data.total_drivers,
      working,
      workingPct: data.total_drivers > 0 ? Math.round((working / data.total_drivers) * 100) : 0,
      dayStart: computeDayStartStats(allBucketDrivers),
    };
  }, [data, getDriverStatus]);

  // When a driver was requested via ?driver=<unit> (e.g. from global search) but
  // the result set is empty while region/dispatcher filters are active, the driver
  // is likely being filtered out server-side. Surface a banner with a clear-filters action.
  const driverParam = searchParams.get('driver') || '';
  const hasServerFilters =
    selectedRegions.length > 0 ||
    selectedDriverDispatchers.length > 0 ||
    selectedOrderDispatchers.length > 0;
  const searchedDriverOutsideFilters = !!(
    driverParam &&
    data &&
    filteredData &&
    hasServerFilters &&
    filteredData.critical_drivers.length === 0 &&
    filteredData.at_risk_drivers.length === 0 &&
    filteredData.on_track_drivers.length === 0 &&
    filteredData.completed_drivers.length === 0
  );

  // Render prop for location map - passed through RiskSection to DriverLane
  const renderLocationMap = useCallback((props: {
    driverLat: number;
    driverLng: number;
    originLat?: number | null;
    originLng?: number | null;
    destLat?: number | null;
    destLng?: number | null;
  }) => {
    if (!isMapLoaded) return <div className="text-sm text-gray-400">Loading map...</div>;
    return (
      <CurrentLocationMiniMap
        driverLat={props.driverLat}
        driverLng={props.driverLng}
        originLat={props.originLat}
        originLng={props.originLng}
        destLat={props.destLat}
        destLng={props.destLng}
      />
    );
  }, [isMapLoaded]);

  return (
    <div className={`${styles.pageContainer} ${showGridBorders ? styles.pageContainerBorders : ''} ${showGridHighlight ? styles.pageContainerHighlight : ''} ${showGridLabels ? styles.pageContainerLabels : ''}`}>
      {/* Filter Bar */}
      <PermissionsContainer componentId="dsm-filter-bar" componentName="Filter Bar">
      <FilterBar label="" className={styles.filterBar} wrap>
        <GridLabel areaName="filterBar" position="top-right" />
        {/* Row 1: data filters. Wraps as a unit onto its own line when the
            viewport is too narrow to hold the controls group beside it, and
            grows a divider underneath itself when it does. */}
        <div
          ref={filterGroupRef}
          className={`relative flex flex-wrap items-center gap-x-3 gap-y-2 min-w-0 ${
            filterBarWrapped
              // The divider is an ::after so it adds no width of its own: any
              // horizontal size change here would feed back into the very wrap
              // it is reacting to. -left-6/-right-6 cancel the bar's px-6 to
              // reach the full width (grow puts this row's edges at the padding
              // box, and grow does not affect flex line breaking).
              ? "grow pb-2 after:content-[''] after:absolute after:-left-6 after:-right-6 after:bottom-0 after:h-px after:bg-gray-200 dark:after:bg-gray-700"
              : ''
          }`}
        >
        <span className="text-sm font-medium text-label whitespace-nowrap">Filters:</span>
        <PermissionAwareInput
          type="date"
          value={selectedDate}
          onChange={(e) => setSelectedDate(e.target.value)}
          className="py-1 px-2 text-xs cursor-pointer bg-white border border-gray-400 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-900 dark:border-gray-500 dark:text-white"
        />
        <OrderDispatcherFilter
          value={selectedOrderDispatchers}
          onChange={setSelectedOrderDispatchers}
        />
        <DriverDispatcherFilter
          value={selectedDriverDispatchers}
          onChange={setSelectedDriverDispatchers}
        />
        <MultiSelect
          options={regionOptions}
          value={selectedRegions}
          onChange={setSelectedRegions}
          placeholder="All Regions"
          showClearOption={true}
          clearOptionLabel="All Regions"
          enableSearch={true}
          searchPlaceholder="Search regions..."
          size="sm"
          className="min-w-[130px]"
        />
        <MultiSelect
          options={[{ value: 'LOCAL', label: 'Local' }, { value: 'OTR', label: 'OTR' }]}
          value={selectedTruckTypes}
          onChange={setSelectedTruckTypes}
          placeholder="All Truck Types"
          showClearOption={true}
          clearOptionLabel="All Truck Types"
          size="sm"
          className="min-w-[130px]"
        />
        <MultiSelect
          options={driverStatusOptions}
          value={selectedStatuses}
          onChange={setSelectedStatuses}
          placeholder="All Statuses"
          showClearOption={true}
          clearOptionLabel="All Statuses"
          size="sm"
          className="min-w-[130px]"
        />
        <MultiSelect
          options={dutyStatusOptions}
          value={selectedDutyStatuses}
          onChange={setSelectedDutyStatuses}
          placeholder="All Duty Statuses"
          showClearOption={true}
          clearOptionLabel="All Duty Statuses"
          size="sm"
          className="min-w-[130px]"
        />
        <MultiSelect
          options={sizeOptions}
          value={selectedSizes}
          onChange={setSelectedSizes}
          placeholder="All Sizes"
          showClearOption={true}
          clearOptionLabel="All Sizes"
          size="sm"
          className="min-w-[130px]"
        />
        <MultiSelect
          options={chassisOptions}
          value={selectedChassisTypes}
          onChange={setSelectedChassisTypes}
          placeholder="All Chassis"
          showClearOption={true}
          clearOptionLabel="All Chassis"
          size="sm"
          className="min-w-[130px]"
        />
        </div>
        {/* Row 2 (narrow screens): Requests toggle through the settings cog. */}
        <div ref={controlsGroupRef} className="flex flex-wrap items-center gap-x-3 gap-y-2 grow min-w-0">
        <div className="flex items-center gap-1.5">
          <span className="text-sm text-gray-600 dark:text-gray-300">Requests</span>
          <Toggle
            checked={showOnlyRequests}
            onChange={setShowOnlyRequests}
            size="sm"
          />
        </div>
        <FilterBar.Divider />
        <div className="flex items-center gap-1">
          <span className="text-sm text-gray-600 dark:text-gray-300">Sort:</span>
          <div className="relative">
            <PermissionAwareSelect
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
              className="py-1 ps-2 pe-7 text-xs cursor-pointer bg-white border border-gray-400 rounded-lg text-start focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-900 dark:border-gray-500 dark:text-white appearance-none min-w-[120px]"
            >
              {sortOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </PermissionAwareSelect>
            <ChevronUpDownIcon className="absolute right-1.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
          </div>
          <button
            onClick={() => setSortDirection(d => d === 'asc' ? 'desc' : 'asc')}
            className="p-1 rounded-lg border border-gray-400 bg-white hover:bg-gray-50 dark:bg-gray-900 dark:border-gray-500 dark:hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
            title={sortDirection === 'asc' ? 'Ascending - click for descending' : 'Descending - click for ascending'}
          >
            {sortDirection === 'asc' ? (
              <ArrowUpIcon className="w-4 h-4 text-gray-600 dark:text-gray-300" />
            ) : (
              <ArrowDownIcon className="w-4 h-4 text-gray-600 dark:text-gray-300" />
            )}
          </button>
        </div>
        <FilterBar.Divider />
        <div className="relative">
          <MagnifyingGlassIcon className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <PermissionAwareInput
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Filter drivers, plates, PROs, locations..."
            className="pl-8 pr-8 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-panel text-body placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent w-56"
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="w-4 h-4" />
            </button>
          )}
        </div>
        {displaySettings.cardOptions?.superCompactView && (
          <>
            <FilterBar.Divider />
            <div className="flex items-center gap-1">
              <span className="text-xs text-muted">Font:</span>
              <button
                onClick={() => setDisplaySettings(prev => ({
                  ...prev,
                  cardOptions: { ...prev.cardOptions, superCompactFontSize: Math.max(7, (prev.cardOptions?.superCompactFontSize ?? 9) - 1) },
                }))}
                className="w-5 h-5 flex items-center justify-center rounded border border-gray-300 dark:border-gray-600 bg-panel text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 text-xs font-bold leading-none"
                title="Decrease font size"
              >−</button>
              <span className="text-xs text-gray-600 dark:text-gray-300 w-4 text-center tabular-nums">
                {displaySettings.cardOptions?.superCompactFontSize ?? 9}
              </span>
              <button
                onClick={() => setDisplaySettings(prev => ({
                  ...prev,
                  cardOptions: { ...prev.cardOptions, superCompactFontSize: Math.min(14, (prev.cardOptions?.superCompactFontSize ?? 9) + 1) },
                }))}
                className="w-5 h-5 flex items-center justify-center rounded border border-gray-300 dark:border-gray-600 bg-panel text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 text-xs font-bold leading-none"
                title="Increase font size"
              >+</button>
            </div>
          </>
        )}
        <FilterBar.Spacer />
        <div className="flex items-center gap-2">
          <Toggle
            checked={showCompleted}
            onChange={setShowCompleted}
            size="sm"
          />
          <span className="text-sm text-gray-600 dark:text-gray-300">Show Completed</span>
        </div>
        <FilterBar.Divider />
        <button
          onClick={() => setSettingsDrawerOpen(true)}
          className="p-1 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
          title="Display Settings"
        >
          <Cog6ToothIcon className="w-4 h-4" />
        </button>
        </div>
      </FilterBar>
      </PermissionsContainer>

      {/* Stat Bar */}
      <div className={`${styles.statBar} flex items-center gap-4 px-6 py-2 bg-theme-bg-secondary border-b border-theme-border-primary text-sm dark:text-white`}>
        <div className={`${styles.statItem} cursor-pointer`} onClick={() => scrollToSection('section-critical', 'critical')}>
          <span className={`${styles.statDot} ${styles.statDotCritical}`} />
          <span>{stats.critical} Critical</span>
        </div>
        <div className={`${styles.statItem} cursor-pointer`} onClick={() => scrollToSection('section-atRisk', 'atRisk')}>
          <span className={`${styles.statDot} ${styles.statDotHigh}`} />
          <span>{stats.atRisk} At Risk</span>
        </div>
        <div className={`${styles.statItem} cursor-pointer`} onClick={() => scrollToSection('section-onTrack', 'onTrack')}>
          <span className={`${styles.statDot} ${styles.statDotOnTrack}`} />
          <span>{stats.onTrack} On Track</span>
        </div>
        <div className={`${styles.statItem} cursor-pointer`} onClick={() => scrollToSection('section-completed', 'completed')}>
          <span className={`${styles.statDot} ${styles.statDotCompleted}`} />
          <span>{stats.completed} Completed</span>
        </div>
        <div className="flex-1" />
        {selectedDate === new Date().toLocaleDateString('en-CA') && (
          <span
            className="border-r border-theme-border-primary pr-4"
            title="Started = on-duty HOS status (driving / on duty / yard move) with a move underway or completed today. On Duty = current Samsara duty status is driving, on duty, or yard move. Expected = drivers with moves assigned today. Drivers missing HOS data are not counted as on duty."
          >
            Started {stats.dayStart.started} · On Duty {stats.dayStart.onDuty} · Expected {stats.dayStart.expected}
          </span>
        )}
        <span>{stats.total} ({stats.workingPct}% working) drivers, {data?.total_moves ?? 0} moves</span>
      </div>

      {/* Unsaved priority changes banner — surfaced page-level so edits aren't
          lost behind a scroll. Each chip jumps to (and expands) the driver's
          lane. Locked drivers are held back from refresh-merges in fetchDrivers. */}
      {pendingReorderUnits.length > 0 && (
        <div className="flex items-center gap-2 px-6 py-2 bg-amber-50 dark:bg-amber-900/30 border-b border-amber-300 dark:border-amber-700 text-sm">
          <ExclamationTriangleIcon className="w-4 h-4 text-amber-700 dark:text-amber-400 flex-shrink-0" />
          <span className="text-amber-800 dark:text-amber-300 font-medium">
            Unsaved priority changes for {pendingReorderUnits.length === 1 ? '1 driver' : `${pendingReorderUnits.length} drivers`}:
          </span>
          <div className="flex items-center gap-1.5 flex-wrap">
            {pendingReorderUnits.map(unit => (
              <button
                key={unit}
                type="button"
                onClick={() => scrollToDriver(unit)}
                className="px-2 py-0.5 text-xs font-semibold rounded bg-amber-200 dark:bg-amber-800 text-amber-900 dark:text-amber-100 hover:bg-amber-300 dark:hover:bg-amber-700 transition-colors"
              >
                #{unit}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Body */}
      <PermissionsContainer componentId="dsm-content" componentName="Driver Status Content">
      <div className={`${styles.body} overflow-auto`} style={{ position: 'relative' }}>
        <GridLabel areaName="body" position="top-right" />

        {/* Loading */}
        {loading && !data && (
          <div className="flex flex-col items-center justify-center h-64 gap-3">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
            <div className="text-sm text-muted">
              Loading driver lanes...
              {elapsedSeconds > 0 && (
                <span className="ml-2">({elapsedSeconds}s)</span>
              )}
            </div>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="m-4 rounded-md bg-red-50 dark:bg-red-900/20 p-4">
            <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
          </div>
        )}

        {/* Reassigning/Reordering overlay */}
        {(reassigning || reordering) && (
          <div className="absolute inset-0 bg-white/50 dark:bg-gray-900/50 flex items-center justify-center z-40">
            <div className="flex items-center gap-2 bg-panel px-4 py-2 rounded-lg shadow-lg">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600" />
              <span>{reordering ? 'Reordering...' : 'Reassigning...'}</span>
            </div>
          </div>
        )}

        {/* Applying Filters overlay */}
        {loading && data && (
          <div className="fixed inset-0 bg-white/50 dark:bg-gray-900/50 flex items-center justify-center z-50">
            <div className="flex items-center gap-2 bg-panel px-4 py-2 rounded-lg shadow-lg">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600" />
              <span>
                Applying Filters...
                {elapsedSeconds > 0 && (
                  <span className="ml-2">({elapsedSeconds}s)</span>
                )}
              </span>
            </div>
          </div>
        )}

        {/* Content */}
        {data && filteredData && !error && (
          <>
            {/* Requesting Work Section (idle drivers requesting dispatches) */}
            {filteredData.requesting_drivers.length > 0 && (
              <RiskSection
                sectionId="section-requesting"
                title="Requesting Work"
                icon={<HandRaisedIcon className="w-5 h-5" />}
                headerClass={styles.sectionHeaderRequesting}
                drivers={filteredData.requesting_drivers}
                showCompleted={showCompleted}
                displaySettings={displaySettings}
                orientation="horizontal"
                renderLocationMap={renderLocationMap}
                onProClick={openOrderDrawer}
                onRemove={handleRemove}
                onQuickAssign={handleQuickAssign}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                onReorder={handleReorder}
                dragTargetDriver={dragTargetDriver}
                dragState={dragState}
                isSectionExpanded={expandedSections.has('requesting')}
                onToggleSection={() => toggleSection('requesting')}
                openAvailableMovesDrawer={openAvailableMovesDrawer}
                onOpenDeadheadMap={openDeadheadMap}
                onGhostCardClick={handleGhostCardClick}
                showGhostCards={false}
                userEmail={permissions?.user_email}
                pendingReorderDrivers={pendingReorderDrivers}
                onApplyReorder={handleApplyReorder}
                onCancelReorder={handleCancelReorder}
                isToday={data?.is_today}
                dispatchRequestUnitnumbers={dispatchRequestUnitnumbers}
                onAcknowledgeRequest={handleAcknowledgeRequest}
                onEditComment={handleEditComment}
                pageDate={selectedDate}
              />
            )}

            {/* Critical Section */}
            <RiskSection
              sectionId="section-critical"
              title="Critical Risk"
              icon={<ExclamationTriangleIcon className="w-5 h-5" />}
              headerClass={styles.sectionHeaderCritical}
              drivers={filteredData.critical_drivers}
              showCompleted={showCompleted}
              displaySettings={displaySettings}
              orientation="horizontal"
              renderLocationMap={renderLocationMap}
              onProClick={openOrderDrawer}
              onRemove={handleRemove}
              onQuickAssign={handleQuickAssign}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              onReorder={handleReorder}
              dragTargetDriver={dragTargetDriver}
              dragState={dragState}
              isSectionExpanded={expandedSections.has('critical')}
              onToggleSection={() => toggleSection('critical')}
              openAvailableMovesDrawer={openAvailableMovesDrawer}
              onOpenDeadheadMap={openDeadheadMap}
              onGhostCardClick={handleGhostCardClick}
              showGhostCards={false}
              userEmail={permissions?.user_email}
              pendingReorderDrivers={pendingReorderDrivers}
              onApplyReorder={handleApplyReorder}
              onCancelReorder={handleCancelReorder}
              isToday={data?.is_today}
              dispatchRequestUnitnumbers={dispatchRequestUnitnumbers}
              onEditComment={handleEditComment}
              pageDate={selectedDate}
            />

            {/* At Risk Section */}
            <RiskSection
              sectionId="section-atRisk"
              title="At Risk"
              icon={<ClockIcon className="w-5 h-5" />}
              headerClass={styles.sectionHeaderHigh}
              drivers={filteredData.at_risk_drivers}
              showCompleted={showCompleted}
              displaySettings={displaySettings}
              orientation="horizontal"
              renderLocationMap={renderLocationMap}
              onProClick={openOrderDrawer}
              onRemove={handleRemove}
              onQuickAssign={handleQuickAssign}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              onReorder={handleReorder}
              dragTargetDriver={dragTargetDriver}
              dragState={dragState}
              isSectionExpanded={expandedSections.has('atRisk')}
              onToggleSection={() => toggleSection('atRisk')}
              openAvailableMovesDrawer={openAvailableMovesDrawer}
              onOpenDeadheadMap={openDeadheadMap}
              onGhostCardClick={handleGhostCardClick}
              showGhostCards={false}
              userEmail={permissions?.user_email}
              pendingReorderDrivers={pendingReorderDrivers}
              onApplyReorder={handleApplyReorder}
              onCancelReorder={handleCancelReorder}
              isToday={data?.is_today}
              dispatchRequestUnitnumbers={dispatchRequestUnitnumbers}
              onEditComment={handleEditComment}
              pageDate={selectedDate}
            />

            {/* On Track Section */}
            <RiskSection
              sectionId="section-onTrack"
              title="On Track"
              icon={<CheckCircleIcon className="w-5 h-5" />}
              headerClass={styles.sectionHeaderOnTrack}
              drivers={filteredData.on_track_drivers}
              showCompleted={showCompleted}
              displaySettings={displaySettings}
              orientation="horizontal"
              renderLocationMap={renderLocationMap}
              onProClick={openOrderDrawer}
              onRemove={handleRemove}
              onQuickAssign={handleQuickAssign}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              onReorder={handleReorder}
              dragTargetDriver={dragTargetDriver}
              dragState={dragState}
              isSectionExpanded={expandedSections.has('onTrack')}
              onToggleSection={() => toggleSection('onTrack')}
              openAvailableMovesDrawer={openAvailableMovesDrawer}
              onOpenDeadheadMap={openDeadheadMap}
              onGhostCardClick={handleGhostCardClick}
              showGhostCards={false}
              userEmail={permissions?.user_email}
              pendingReorderDrivers={pendingReorderDrivers}
              onApplyReorder={handleApplyReorder}
              onCancelReorder={handleCancelReorder}
              isToday={data?.is_today}
              dispatchRequestUnitnumbers={dispatchRequestUnitnumbers}
              onEditComment={handleEditComment}
              pageDate={selectedDate}
            />

            {/* Completed Section */}
            <RiskSection
              sectionId="section-completed"
              title="Completed"
              icon={<CheckCircleIcon className="w-5 h-5" />}
              headerClass={styles.sectionHeaderCompleted}
              drivers={filteredData.completed_drivers}
              showCompleted={showCompleted}
              displaySettings={displaySettings}
              orientation="horizontal"
              renderLocationMap={renderLocationMap}
              onProClick={openOrderDrawer}
              onRemove={handleRemove}
              onQuickAssign={handleQuickAssign}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              onReorder={handleReorder}
              dragTargetDriver={dragTargetDriver}
              dragState={dragState}
              isSectionExpanded={expandedSections.has('completed')}
              onToggleSection={() => toggleSection('completed')}
              openAvailableMovesDrawer={openAvailableMovesDrawer}
              onOpenDeadheadMap={openDeadheadMap}
              onGhostCardClick={handleGhostCardClick}
              showGhostCards={false}
              userEmail={permissions?.user_email}
              pendingReorderDrivers={pendingReorderDrivers}
              onApplyReorder={handleApplyReorder}
              onCancelReorder={handleCancelReorder}
              isToday={data?.is_today}
              dispatchRequestUnitnumbers={dispatchRequestUnitnumbers}
              onEditComment={handleEditComment}
              pageDate={selectedDate}
            />

            {/* Searched driver is outside current region/dispatcher filters */}
            {searchedDriverOutsideFilters && (
              <div className="flex flex-col items-center justify-center h-64 text-gray-500 gap-2">
                <MagnifyingGlassIcon className="w-12 h-12 mb-2" />
                <p>Driver &ldquo;{driverParam}&rdquo; is not in the current results</p>
                <p className="text-sm">They may be outside your active region or dispatcher filters.</p>
                <button
                  type="button"
                  onClick={handleClearServerFilters}
                  className="mt-2 px-4 py-2 text-sm font-medium rounded-md bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  Clear region and dispatcher filters
                </button>
              </div>
            )}

            {/* Empty state */}
            {data.total_drivers === 0 && !searchedDriverOutsideFilters && (
              <div className="flex flex-col items-center justify-center h-64 text-gray-500">
                <UserIcon className="w-12 h-12 mb-2" />
                <p>No drivers with active moves found</p>
                <p className="text-sm">Try adjusting your filters</p>
              </div>
            )}

            {/* No search results */}
            {searchTerm && filteredData.critical_drivers.length === 0 && filteredData.at_risk_drivers.length === 0 && filteredData.on_track_drivers.length === 0 && filteredData.completed_drivers.length === 0 && data.total_drivers > 0 && !searchedDriverOutsideFilters && (
              <div className="flex flex-col items-center justify-center h-64 text-gray-500">
                <MagnifyingGlassIcon className="w-12 h-12 mb-2" />
                <p>No results found for &ldquo;{searchTerm}&rdquo;</p>
                <p className="text-sm">Try a different search term</p>
              </div>
            )}
          </>
        )}
      </div>
      </PermissionsContainer>

      {/* Remove Confirm Modal */}
      {removeMove && (
        <RemoveConfirmModal
          move={removeMove}
          group={removeGroup}
          loading={removing}
          onClose={() => {
            setRemoveMove(null);
            setRemoveGroup(null);
          }}
          onConfirm={handleConfirmRemove}
        />
      )}

      {/* Deadline Reason Note Modal */}
      {showNoteModal && (
        <ReasonNoteModal
          onClose={handleNoteCancel}
          onSubmit={onNoteSubmit}
        />
      )}

      {/* Driver Note Modal (lane header comment icon) */}
      {commentModal && (
        <DriverCommentModal
          unitnumber={commentModal.unitnumber}
          driverName={commentModal.driverName}
          initialComment={commentModal.comment}
          saving={commentSaving}
          onSave={handleSaveComment}
          onClose={() => setCommentModal(null)}
        />
      )}

      {/* Ghost Assign Modal */}
      {ghostModalData && (
        <GhostAssignModal
          suggestion={ghostModalData.suggestion}
          driverName={ghostModalData.driverName}
          onConfirm={handleGhostAssign}
          onCancel={() => setGhostModalData(null)}
        />
      )}

      {/* Settings Drawer */}
      <SettingsDrawer
        isOpen={settingsDrawerOpen}
        onClose={() => setSettingsDrawerOpen(false)}
        displaySettings={displaySettings}
        onSettingsChange={setDisplaySettings}
      />

      {/* Order Details Drawer */}
      <ProDetailSlideOver
        pronumber={selectedPro}
        isOpen={isOrderDrawerOpen && selectedPro !== null}
        onClose={closeOrderDrawer}
      />

      {/* Available Moves Drawer */}
      {availableMovesDrawerOpen && createPortal(
        <PermissionsContainer componentId="dsm-available-moves" componentName="Available Moves">
          <div className={styles.drawerOverlay} onClick={closeAvailableMovesDrawer} />
          <div className={styles.availableMovesDrawer}>
            <button
              className={styles.drawerCloseButton}
              onClick={closeAvailableMovesDrawer}
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
            <div className={styles.drawerHeader}>
              <span className={styles.drawerTitle}>
                Available Moves for {availableMovesDriverUnit} - {availableMovesDriverName}
              </span>
            </div>
            {/* Sticky section: Map + Search */}
            <div className={styles.drawerStickySection}>
              {drawerMapData && isMapLoaded && (
                <div className={styles.drawerStickyMap}>
                  <div className={styles.drawerMapRoute}>
                    <div className={styles.drawerMapStop}>
                      <div className={styles.drawerMapStopDot} style={{ backgroundColor: 'rgb(var(--c-red-500))' }} />
                      <div>
                        <div className={styles.drawerMapStopName}>{drawerMapData.fromName}</div>
                        <div className={styles.drawerMapStopCity}>{drawerMapData.fromCityState}</div>
                      </div>
                    </div>
                    <div className={styles.drawerMapArrow}>→</div>
                    <div className={styles.drawerMapStop}>
                      <div className={styles.drawerMapStopDot} style={{ backgroundColor: 'rgb(var(--c-emerald-500))' }} />
                      <div>
                        <div className={styles.drawerMapStopName}>{drawerMapData.toName}</div>
                        <div className={styles.drawerMapStopCity}>{drawerMapData.toCityState}</div>
                      </div>
                    </div>
                  </div>
                  <div className={styles.drawerMapContainer}>
                    <AvailableMovesMap
                      fromLat={drawerMapData.fromLat}
                      fromLng={drawerMapData.fromLng}
                      toLat={drawerMapData.toLat}
                      toLng={drawerMapData.toLng}
                      availablePros={availableMoves}
                      onBoundsChanged={setDrawerMapBounds}
                      onProClick={setSelectedMapPros}
                      selectedPros={selectedMapPros}
                    />
                  </div>
                </div>
              )}
              <div className={styles.drawerStickySearch}>
                <div className={styles.drawerSearchWrapper}>
                  <MagnifyingGlassIcon className={styles.drawerSearchIcon} />
                  <PermissionAwareInput
                    type="text"
                    placeholder="Search PRO, customer, location..."
                    value={availableMovesSearch}
                    onChange={(e) => setAvailableMovesSearch(e.target.value)}
                    className={styles.drawerSearchInput}
                  />
                </div>
                {selectedMapPros.length > 0 && (
                  <div className={styles.drawerFilterIndicator}>
                    <span>
                      Showing {selectedMapPros.length} selected PRO{selectedMapPros.length > 1 ? 's' : ''} from map
                    </span>
                    <button
                      onClick={() => setSelectedMapPros([])}
                      className={styles.drawerFilterClear}
                    >
                      Show All
                    </button>
                  </div>
                )}
              </div>
            </div>
            <div className={styles.drawerContent}>
              {availableMovesLoading ? (
                <div className={styles.drawerLoading}>
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
                </div>
              ) : availableMovesError ? (
                <div className={styles.drawerEmpty}>
                  Couldn't load available moves — the request failed. Close and reopen the drawer to retry.
                </div>
              ) : availableMoves.length === 0 ? (
                <div className={styles.drawerEmpty}>No available moves found</div>
              ) : (
                <>
                  {groupedAvailableMoves.map((group: any[]) => {
                    const renderProCard = (pro: any, showAssign: boolean, isLinkedGroup: boolean) => {
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);

                      return (
                        <div className={`${styles.drawerProCard} ${isLinkedGroup ? styles.drawerProCardLinked : ''}`}>
                          <div className={styles.drawerProHeader}>
                            <div className={styles.drawerProHeaderLeft}>
                              <a
                                href={proDetailPath(pro.pronumber)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={styles.drawerProNumber}
                                onClick={(e) => e.stopPropagation()}
                              >
                                {pro.pronumber}-{pro.legnum}
                              </a>
                              <a
                                href={proDetailPath(pro.pronumber)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={styles.drawerProExternalLink}
                                onClick={(e) => e.stopPropagation()}
                                title="Open in GWI360"
                              >
                                <ArrowTopRightOnSquareIcon className="w-4 h-4" />
                              </a>
                            </div>
                            {/* Key Account / New Location Badge */}
                            {(() => {
                              // New Location takes priority
                              if (pro.destIsNewLocation === 'Y') {
                                return (
                                  <span className="px-2 py-0.5 text-[0.6875rem] font-medium rounded text-white bg-blue-500">
                                    NEW
                                  </span>
                                );
                              }
                              // Key Account badges
                              const keyAccount = (pro.keyAccount || '').toUpperCase();
                              if (keyAccount === 'KEY ACCOUNTS 1') {
                                return (
                                  <span className="px-2 py-0.5 text-[0.6875rem] font-medium rounded text-white bg-red-500">
                                    KEY 1
                                  </span>
                                );
                              }
                              if (keyAccount === 'KEY ACCOUNTS 2') {
                                return (
                                  <span className="px-2 py-0.5 text-[0.6875rem] font-medium rounded text-white bg-orange-500">
                                    KEY 2
                                  </span>
                                );
                              }
                              if (keyAccount === 'KEY ACCOUNTS 3') {
                                return (
                                  <span className="px-2 py-0.5 text-[0.6875rem] font-medium rounded text-black bg-yellow-400">
                                    KEY 3
                                  </span>
                                );
                              }
                              if (keyAccount === 'KEY ACCOUNTS 4') {
                                return (
                                  <span className="px-2 py-0.5 text-[0.6875rem] font-medium rounded text-white bg-green-500">
                                    KEY 4
                                  </span>
                                );
                              }
                              if (keyAccount === 'CONTRACT') {
                                return (
                                  <span className="px-2 py-0.5 text-[0.6875rem] font-medium rounded text-black bg-lime-400">
                                    CONTRACT
                                  </span>
                                );
                              }
                              if (keyAccount === 'KEY PROSPECT 1') {
                                return (
                                  <span className="px-2 py-0.5 text-[0.6875rem] font-medium rounded text-white bg-purple-500">
                                    KP1
                                  </span>
                                );
                              }
                              if (keyAccount === 'KEY PROSPECT 2') {
                                return (
                                  <span className="px-2 py-0.5 text-[0.6875rem] font-medium rounded text-white bg-purple-500">
                                    KP2
                                  </span>
                                );
                              }
                              if (keyAccount === 'KEY PROSPECT 3') {
                                return (
                                  <span className="px-2 py-0.5 text-[0.6875rem] font-medium rounded text-white bg-purple-500">
                                    KP3
                                  </span>
                                );
                              }
                              if (keyAccount === 'KEY PROSPECT 4') {
                                return (
                                  <span className="px-2 py-0.5 text-[0.6875rem] font-medium rounded text-white bg-purple-500">
                                    KP4
                                  </span>
                                );
                              }
                              // Average or unknown - no badge
                              return null;
                            })()}
                            {pro.moveType && (
                              <span className={`${styles.drawerProBadge} ${
                                pro.moveType === 'IMPORT'
                                  ? styles.drawerProBadgeImport
                                  : pro.moveType === 'EXPORT'
                                  ? styles.drawerProBadgeExport
                                  : pro.moveType === 'RAIL TO PORT'
                                  ? styles.drawerProBadgeRailToPort
                                  : styles.drawerProBadgeOther
                              }`}>
                                {pro.moveType}
                              </span>
                            )}
                          </div>

                          {pro.customerName && (
                            <div className={styles.drawerProCustomer}>{pro.customerName}</div>
                          )}

                          {pro.originName && (
                            <div className={styles.drawerProLocation}>
                              <MapPinIcon className={styles.drawerProLocationIcon} />
                              <span>{pro.originName}</span>
                            </div>
                          )}
                          {pro.originCityState && (
                            <div className={styles.drawerProCityState}>{pro.originCityState}</div>
                          )}

                          {pro.destName && pro.destName !== '' && (
                            <>
                              <div className={styles.drawerProLocation}>
                                <MapPinIcon className={styles.drawerProLocationIcon} />
                                <span>{pro.destName}</span>
                              </div>
                              {pro.destName !== 'TBD' && pro.destCityState && pro.destCityState !== '-, -' && (
                                <div className={styles.drawerProCityState}>{pro.destCityState}</div>
                              )}
                            </>
                          )}

                          {pro.destApptDate && pro.destApptDate !== '' && (
                            <div className={styles.drawerProInfo}>
                              Appt: {pro.destApptDate}
                              {pro.destApptTime1 && ` ${pro.destApptTime1}`}
                              {pro.destApptTime2 && `-${pro.destApptTime2}`}
                            </div>
                          )}

                          {pro.moveType && (pro.moveType === 'EXPORT' || pro.moveType === 'RAIL TO PORT') && (pro.perDiemDate || pro.cutoffDatetime) && (() => {
                            const parts: React.ReactNode[] = [];

                            if (pro.perDiemDate && pro.perDiemDate !== '') {
                              const [month, day] = pro.perDiemDate.split('/').map(Number);
                              const perDiemDate = new Date(today.getFullYear(), month - 1, day);
                              const isPastOrToday = perDiemDate <= today;
                              parts.push(
                                <span key="perdiem" className={isPastOrToday ? styles.drawerProDateRed : ''}>
                                  Per Diem: {pro.perDiemDate}
                                </span>
                              );
                            }

                            if (pro.cutoffDatetime && pro.cutoffDatetime !== '') {
                              const [datePart, timePart] = pro.cutoffDatetime.split(' ');
                              const [month, day] = datePart.split('/').map(Number);
                              const [hours, minutes] = timePart.split(':').map(Number);
                              const cutoffDate = new Date(today.getFullYear(), month - 1, day, hours, minutes);
                              const tomorrow = new Date();
                              tomorrow.setDate(tomorrow.getDate() + 1);
                              tomorrow.setHours(0, 0, 0, 0);
                              const isPastOrNow = cutoffDate < tomorrow;
                              parts.push(
                                <span key="cutoff" className={isPastOrNow ? styles.drawerProDateRed : ''}>
                                  Cutoff: {pro.cutoffDatetime}
                                </span>
                              );
                            }

                            if (parts.length === 0) return null;

                            return (
                              <div className={styles.drawerProInfo}>
                                {parts.reduce((acc: any[], part, idx) => {
                                  if (idx === 0) return [part];
                                  return [...acc, ' | ', part];
                                }, [])}
                              </div>
                            );
                          })()}

                          {pro.moveType === 'IMPORT' && (pro.lfdDate || pro.perDiemDate) && (() => {
                            const parts: React.ReactNode[] = [];

                            if (pro.lfdDate && pro.lfdDate !== '') {
                              const [month, day] = pro.lfdDate.split('/').map(Number);
                              const lfdDate = new Date(today.getFullYear(), month - 1, day);
                              const isPastOrToday = lfdDate <= today;
                              parts.push(
                                <span key="lfd" className={isPastOrToday ? styles.drawerProDateRed : ''}>
                                  LFD: {pro.lfdDate}
                                </span>
                              );
                            }

                            if (pro.perDiemDate && pro.perDiemDate !== '') {
                              const [month, day] = pro.perDiemDate.split('/').map(Number);
                              const perDiemDate = new Date(today.getFullYear(), month - 1, day);
                              const isPastOrToday = perDiemDate <= today;
                              parts.push(
                                <span key="perdiem" className={isPastOrToday ? styles.drawerProDateRed : ''}>
                                  Per Diem: {pro.perDiemDate}
                                </span>
                              );
                            }

                            if (parts.length === 0) return null;

                            return (
                              <div className={styles.drawerProInfo}>
                                {parts.reduce((acc: any[], part, idx) => {
                                  if (idx === 0) return [part];
                                  return [...acc, ' | ', part];
                                }, [])}
                              </div>
                            );
                          })()}

                          {pro.size && (
                            <div className={styles.drawerProInfo}>
                              {pro.size}' {pro.equipType?.split(' ').map((word: string) => word.charAt(0) + word.slice(1).toLowerCase()).join(' ')}
                              {pro.chassisType && ` | ${pro.chassisType.split(' ').map((word: string) => word.charAt(0) + word.slice(1).toLowerCase()).join(' ')} Chassis`}
                            </div>
                          )}

                          {(pro.scac || pro.chassisId) && (
                            <div className={styles.drawerProInfo}>
                              {pro.scac && <span>SCAC: {pro.scac}</span>}
                              {pro.scac && pro.chassisId && <span> | </span>}
                              {pro.chassisId && <span>Chassis: {pro.chassisId}</span>}
                            </div>
                          )}

                          {pro.distance !== undefined && (
                            <div className={styles.drawerProDistance}>
                              {pro.distance} miles away
                            </div>
                          )}

                          {showAssign && (
                            <PermissionAwareButton
                              className={styles.assignButton}
                              onClick={() => assignMoveToDriver(isLinkedGroup ? group : pro)}
                              disabled={assigningPro === pro.pronumber}
                            >
                              {assigningPro === pro.pronumber ? 'Assigning...' : (isLinkedGroup ? 'Assign All' : 'Assign')}
                            </PermissionAwareButton>
                          )}
                        </div>
                      );
                    };

                    return group.length > 1 ? (
                      <div key={`group-${group[0].pronumber}`} className={styles.linkedMovesGroup}>
                        {group.map((pro: any, proIndex: number) => (
                          <React.Fragment key={`${pro.pronumber}-${pro.legnum}`}>
                            {proIndex > 0 && (
                              <div className={styles.chainLinkVertical}>
                                <LinkIcon className={styles.chainLinkVerticalIcon} />
                              </div>
                            )}
                            {renderProCard(pro, proIndex === group.length - 1, true)}
                          </React.Fragment>
                        ))}
                      </div>
                    ) : (
                      <React.Fragment key={`${group[0].pronumber}-${group[0].legnum}`}>
                        {renderProCard(group[0], true, false)}
                      </React.Fragment>
                    );
                  })}
                </>
              )}
            </div>
          </div>
        </PermissionsContainer>,
        document.body
      )}

      {/* Deadhead Map Modal */}
      {deadheadMapOpen && deadheadMapData && createPortal(
        <>
          <div className={styles.deadheadMapOverlay} onClick={() => setDeadheadMapOpen(false)} />
          <div className={styles.deadheadMapModal}>
            <div className={styles.deadheadMapHeader}>
              <h3 className={styles.deadheadMapTitle}>
                Deadhead Route ({formatMinutes(deadheadMapData.travelMinutes)})
              </h3>
              <button
                className={styles.deadheadMapClose}
                onClick={() => setDeadheadMapOpen(false)}
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            <div className={styles.deadheadMapRoute}>
              <div className={styles.deadheadMapStop}>
                <div className={styles.deadheadMapStopDot} style={{ backgroundColor: 'rgb(var(--c-red-500))' }} />
                <div>
                  <div className={styles.deadheadMapStopName}>{deadheadMapData.fromName}</div>
                  <div className={styles.deadheadMapStopCity}>{deadheadMapData.fromCityState}</div>
                </div>
              </div>
              <div className={styles.deadheadMapArrow}>→</div>
              <div className={styles.deadheadMapStop}>
                <div className={styles.deadheadMapStopDot} style={{ backgroundColor: 'rgb(var(--c-emerald-500))' }} />
                <div>
                  <div className={styles.deadheadMapStopName}>{deadheadMapData.toName}</div>
                  <div className={styles.deadheadMapStopCity}>{deadheadMapData.toCityState}</div>
                </div>
              </div>
            </div>
            <div className={styles.deadheadMapContainer}>
              {isMapLoaded ? (
                <DeadheadMap
                  fromLat={deadheadMapData.fromLat}
                  fromLng={deadheadMapData.fromLng}
                  toLat={deadheadMapData.toLat}
                  toLng={deadheadMapData.toLng}
                />
              ) : (
                <div className={styles.deadheadMapLoading}>Loading map...</div>
              )}
            </div>
            <div className={styles.deadheadMapFooter}>
              <button
                className={styles.deadheadMapAddMoveButton}
                onClick={() => {
                  setDeadheadMapOpen(false);
                  openAvailableMovesDrawer(deadheadMapData.driverUnit, deadheadMapData.driverName, {
                    fromName: deadheadMapData.fromName,
                    fromCityState: deadheadMapData.fromCityState,
                    fromLat: deadheadMapData.fromLat,
                    fromLng: deadheadMapData.fromLng,
                    toName: deadheadMapData.toName,
                    toCityState: deadheadMapData.toCityState,
                    toLat: deadheadMapData.toLat,
                    toLng: deadheadMapData.toLng,
                    travelMinutes: deadheadMapData.travelMinutes,
                  });
                }}
              >
                Add Available Move
              </button>
            </div>
          </div>
        </>,
        document.body
      )}
    </div>
  );
}

export default DriverStatusMonitor;
